import { useState, useMemo, useEffect, useCallback, useRef, Suspense } from "react";
import { lazyWithChunkReload } from "@/lib/lazy-with-chunk-reload";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileFilterSheet, MobileFilterField } from "@/components/mobile/mobile-filter-sheet";
import { useVoiceAssistantState } from "@/contexts/voice-assistant-state";
import { useEffectivePermissions } from "@/hooks/use-effective-permissions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea } from "@/components/voice-field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { Loader2, Calendar, Upload, Copy, ClipboardPaste, FileSpreadsheet, ChevronLeft, ChevronRight, MapPin, Clock, ChevronsUpDown, Check, Download, RefreshCw, ChevronDown, Sheet, Plus, Bot, AlertCircle, Trash2, Save, Settings, Columns, Settings2, Pencil } from "lucide-react";
import { StatusAdminDialog } from "@/components/status-admin-dialog";
import { ColumnsAdminDialog } from "@/components/columns-admin-dialog";
import { SheetsSyncDialog } from "@/components/sheets-sync-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { ScheduleEntry, Invoice, UserRole } from "@shared/schema";
import { cn } from "@/lib/utils";
import { useResizableColumns } from "@/hooks/use-resizable-columns";
import type { Region } from "@shared/schema";

const TourPlanner = lazyWithChunkReload(() => import("@/components/tour-planner"));
const TourPlanningPanel = lazyWithChunkReload(() => import("@/components/tour-planning-panel").then(m => ({ default: m.TourPlanningPanel })));

interface MasterSchedulePageProps {
  effectiveRole?: UserRole;
}

const DEFAULT_STATUS_OPTIONS = ["SL Shows", "Public Show", "Travel Day", "Outside Show", "Day Off"];

const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAYS_OF_WEEK_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

interface CalendarDayData {
  date: Date;
  entry?: ScheduleEntry;
  invoiceTotal: number;
  isCurrentMonth: boolean;
  isToday: boolean;
}

// Core columns always present in the schedule grid
const CORE_SCHEDULE_COLUMNS = [
  { key: "date", label: "Date", width: "80px" },
  { key: "day", label: "Day", width: "96px" },
  { key: "status", label: "Status", width: "112px" },
  { key: "city", label: "City", width: "112px" },
  { key: "state", label: "State", width: "64px" },
  { key: "notes", label: "Notes", width: "160px" },
  { key: "driveTime", label: "Drive Time", width: "96px" },
  { key: "invoiceTotal", label: "Total Booking $", width: "96px" },
  { key: "region", label: "Region", width: "140px" },
];

// Default widths for known workspace-configurable custom columns
const CUSTOM_COL_WIDTHS: Record<string, string> = {
  optionB: "128px",
  leadPages: "96px",
  estDays: "80px",
};

// Full column list used only for resizable-column hook initialization
const SCHEDULE_COLUMNS = [
  { key: "date", label: "Date", width: "80px" },
  { key: "day", label: "Day", width: "96px" },
  { key: "status", label: "Status", width: "112px" },
  { key: "city", label: "City", width: "112px" },
  { key: "state", label: "State", width: "64px" },
  { key: "optionB", label: "Option B", width: "128px" },
  { key: "notes", label: "Notes", width: "160px" },
  { key: "driveTime", label: "Drive Time", width: "96px" },
  { key: "leadPages", label: "Lead Pages", width: "96px" },
  { key: "estDays", label: "Est. Days", width: "80px" },
  { key: "invoiceTotal", label: "Total Booking $", width: "96px" },
  { key: "region", label: "Region", width: "140px" },
];

const SCHEDULE_IMPORT_FIELDS = [
  { key: "date", label: "Date", required: true },
  { key: "status", label: "Status" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "optionB", label: "Option B" },
  { key: "notes", label: "Notes" },
  { key: "driveTime", label: "Drive Time" },
  { key: "leadPages", label: "Lead Pages" },
  { key: "optionC", label: "Option C" },
  { key: "optionD", label: "Option D" },
  { key: "region", label: "Region" },
];

// Editable fields in Tab order for spreadsheet navigation
const EDITABLE_FIELDS = ["city", "state", "optionB", "notes", "driveTime", "leadPages"];

interface ScheduleRowData {
  date: Date;
  dayOfWeek: string;
  entry?: ScheduleEntry;
  invoiceTotal: number;
}

export default function MasterSchedulePage({ effectiveRole }: MasterSchedulePageProps) {
  const today = new Date();
  const isMobile = useIsMobile();
  const { permissions } = useEffectivePermissions();
  const tourPlannerAllowed = permissions["schedule.feature.tourPlanner"] === true;
  // Task #611 — added "tour" view mode (Tour Mode planner). On mobile we
  // still snap to list view by default; tour mode is admin-driven and
  // primarily a desktop surface, so we don't auto-snap to it.
  const [viewMode, setViewMode] = useState<"spreadsheet" | "calendar" | "list" | "tour">("spreadsheet");

  // Lock to list view on mobile — spreadsheet and calendar are not usable on small screens
  useEffect(() => {
    if (isMobile && viewMode !== "list" && viewMode !== "tour") {
      setViewMode("list");
    }
  }, [isMobile, viewMode]);

  // Feature Visibility gate: if Tour Planner is hidden for this role/tier,
  // never leave the user stranded on the tour view.
  useEffect(() => {
    if (!tourPlannerAllowed && viewMode === "tour") {
      setViewMode("spreadsheet");
    }
  }, [tourPlannerAllowed, viewMode]);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [selectedTalentId, setSelectedTalentIdRaw] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("master-schedule-talent-id") || "";
  });
  const setSelectedTalentId = (id: string) => {
    setSelectedTalentIdRaw(id);
    if (typeof window !== "undefined") {
      if (id) localStorage.setItem("master-schedule-talent-id", id);
      else localStorage.removeItem("master-schedule-talent-id");
    }
  };
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth());
  const [editingCell, setEditingCell] = useState<{ date: string; field: string } | null>(null);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [notesDialogRow, setNotesDialogRow] = useState<ScheduleRowData | null>(null);
  const [notesDialogValue, setNotesDialogValue] = useState("");
  const [openRegionPopover, setOpenRegionPopover] = useState<string | null>(null);
  const [addingRegionForRow, setAddingRegionForRow] = useState<string | null>(null);
  const [newRegionName, setNewRegionName] = useState("");
  const [editValue, setEditValue] = useState("");
  const [isSheetsSyncOpen, setIsSheetsSyncOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importData, setImportData] = useState<string>("");
  const [isImporting, setIsImporting] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvDataRows, setCsvDataRows] = useState<string[][]>([]);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Task #116 — drawer state is hoisted into the voice-assistant context so
  // the floating orb (rendered by VoiceAssistantWidget) and the Tour Planning
  // drawer can share a single Framer-Motion layoutId and morph in the same
  // render commit. localStorage persistence happens inside the context.
  const { tourPanelOpen: aiPanelOpen, setTourPanelOpen } = useVoiceAssistantState();
  const [activeDraftGroupId, setActiveDraftGroupId] = useState<string | undefined>(undefined);
  const [showStatusAdmin, setShowStatusAdmin] = useState(false);
  const [showColumnsAdmin, setShowColumnsAdmin] = useState(false);
  // Task #289 — Cell selection for spreadsheet-like navigation
  const [selectedCell, setSelectedCell] = useState<{ dateKey: string; field: string } | null>(null);
  // Task #289 — Sandbox mode: isolates edits from live data until published
  const [sandboxMode, setSandboxMode] = useState(false);
  const [sandboxEntries, setSandboxEntries] = useState<Record<string, Record<string, any>>>({});
  const [sandboxSnapshot, setSandboxSnapshot] = useState<ScheduleRowData[] | null>(null);
  const [isSandboxPublishing, setIsSandboxPublishing] = useState(false);
  // Task #289 — Fill handle drag state
  const [fillDrag, setFillDrag] = useState<{ startKey: string; field: string; value: string } | null>(null);
  const [fillDragEndKey, setFillDragEndKey] = useState<string | null>(null);
  // Range selection: selectionEnd extends from selectedCell (anchor) to form a rectangle
  const [selectionEnd, setSelectionEnd] = useState<{ dateKey: string; field: string } | null>(null);
  const [isSelectDragging, setIsSelectDragging] = useState(false);
  const [pendingRegions, setPendingRegions] = useState<string[] | null>(null);
  const [pendingCommitId, setPendingCommitId] = useState<string | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableScrollTop, setTableScrollTop] = useState(0);
  const handleTableScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setTableScrollTop(e.currentTarget.scrollTop);
  }, []);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const { getColumnWidth, handleResizeStart } = useResizableColumns({
    columns: SCHEDULE_COLUMNS,
    storageKey: "schedule-column-widths",
  });

  const { data: talent = [] } = useQuery<Array<{ id: string; name: string; isDefault?: boolean; isActive?: boolean }>>({
    queryKey: ["/api/talent"],
  });

  const { data: workspaceColumns = [] } = useQuery<Array<{
    id: string; workspaceId: string; key: string; label: string;
    dataType: string; sortOrder: number; isVisible: boolean; createdAt: string;
  }>>({
    queryKey: ["/api/schedule-columns"],
  });

  // Default to the workspace's default talent (or first active one) when nothing is selected yet
  useEffect(() => {
    if (selectedTalentId) return;
    if (!talent.length) return;
    const def = talent.find((e) => e.isDefault) || talent.find((e) => e.isActive !== false) || talent[0];
    if (def) setSelectedTalentId(def.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talent, selectedTalentId]);

  // Fetch workspace-configurable status options (Task #59)
  const { data: workspaceStatuses = [] } = useQuery<Array<{ id: string; label: string; color: string; isSystemLocked: boolean; sortOrder: number }>>({
    queryKey: ["/api/schedule-statuses"],
  });
  const statusOptions = workspaceStatuses.length > 0
    ? workspaceStatuses.map((s) => s.label)
    : DEFAULT_STATUS_OPTIONS;

  // Fetch schedule runs for current year/talent (Task #59)
  const { data: scheduleRunsData = [] } = useQuery<Array<{
    id: string; startDate: string; endDate: string; city: string | null; state: string | null; entryIds: string[]; dayCount: number;
  }>>({
    queryKey: ["/api/schedule/runs", selectedYear, selectedTalentId],
    queryFn: async () => {
      const params = new URLSearchParams({ year: selectedYear.toString() });
      if (selectedTalentId) params.set("talentId", selectedTalentId);
      const resp = await fetch(`/api/schedule/runs?${params}`, { credentials: "include" });
      if (!resp.ok) return [];
      return resp.json();
    },
    enabled: !!selectedTalentId,
  });

  // Build a map: dateStr → run info (for visual treatment)
  const runsByDate = useMemo(() => {
    const map = new Map<string, { runId: string; isFirst: boolean; dayCount: number; city: string | null }>();
    for (const run of scheduleRunsData) {
      const startStr = run.startDate.slice(0, 10);
      const endDate = new Date(run.endDate);
      const startDate = new Date(run.startDate);
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const ds = d.toISOString().slice(0, 10);
        map.set(ds, { runId: run.id, isFirst: ds === startStr, dayCount: run.dayCount, city: run.city });
      }
    }
    return map;
  }, [scheduleRunsData]);

  // Workspace custom columns split into two groups by insertion position
  const preNoteCustom = useMemo(
    () => workspaceColumns
      .filter((c) => c.isVisible && !["leadPages", "estDays"].includes(c.key))
      .sort((a, b) => a.sortOrder - b.sortOrder),
    [workspaceColumns],
  );
  const postDriveCustom = useMemo(
    () => workspaceColumns
      .filter((c) => c.isVisible && ["leadPages", "estDays"].includes(c.key))
      .sort((a, b) => a.sortOrder - b.sortOrder),
    [workspaceColumns],
  );
  // Merged thead column list: core cols with workspace custom cols injected
  const mergedColumns = useMemo(() => {
    const preNote = preNoteCustom.map((c) => ({ key: c.key, label: c.label, width: CUSTOM_COL_WIDTHS[c.key] ?? "96px" }));
    const postDrive = postDriveCustom.map((c) => ({ key: c.key, label: c.label, width: CUSTOM_COL_WIDTHS[c.key] ?? "96px" }));
    return [
      ...CORE_SCHEDULE_COLUMNS.slice(0, 5), // date, day, status, city, state
      ...preNote,
      ...CORE_SCHEDULE_COLUMNS.slice(5, 7), // notes, driveTime
      ...postDrive,
      ...CORE_SCHEDULE_COLUMNS.slice(7),    // invoiceTotal, region
    ];
  }, [preNoteCustom, postDriveCustom]);

  // Build run summary string for AI context
  const runSummary = useMemo(() => {
    if (!scheduleRunsData.length) return "";
    return scheduleRunsData
      .slice(0, 10)
      .map((r) => `${r.city ?? "?"},${r.endDate.slice(0, 10).slice(5)}: ${r.dayCount} days`)
      .join("; ");
  }, [scheduleRunsData]);

  const { data: scheduleEntries = [], isLoading: entriesLoading } = useQuery<ScheduleEntry[]>({
    queryKey: ["/api/schedule", selectedYear, selectedTalentId],
    queryFn: async () => {
      const url = selectedTalentId
        ? `/api/schedule/${selectedYear}?talentId=${encodeURIComponent(selectedTalentId)}`
        : `/api/schedule/${selectedYear}`;
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch schedule");
      return response.json();
    },
    enabled: !!selectedTalentId,
  });

  const { data: invoices = [] } = useQuery<Invoice[]>({
    queryKey: ["/api/invoices"],
  });

  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ["/api/regions"],
  });

  const updateEntryMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ScheduleEntry> }) => {
      const resp = await apiRequest("PATCH", `/api/schedule/${id}`, updates);
      return resp.json() as Promise<ScheduleEntry>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/schedule", selectedYear, selectedTalentId] });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update schedule entry.", variant: "destructive" });
    },
  });

  const createEntryMutation = useMutation({
    mutationFn: async (data: { year: number; date: Date; [key: string]: any }) => {
      const resp = await apiRequest("POST", "/api/schedule", { ...data, talentId: selectedTalentId || null });
      return resp.json() as Promise<ScheduleEntry>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/schedule", selectedYear, selectedTalentId] });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create schedule entry.", variant: "destructive" });
    },
  });

  const bulkUpdateEntryMutation = useMutation({
    mutationFn: async (updates: Array<{ id: string; updates: Record<string, any> }>) => {
      const resp = await apiRequest("PATCH", "/api/schedule/bulk-update", { updates });
      return resp.json() as Promise<{ results: any[] }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/schedule", selectedYear, selectedTalentId] });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update schedule entries.", variant: "destructive" });
    },
  });

  const createRegionMutation = useMutation({
    mutationFn: async (name: string) => {
      return apiRequest("POST", "/api/regions", { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/regions"] });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create region.", variant: "destructive" });
    },
  });

  const generateYearDates = useCallback((year: number): Date[] => {
    const dates: Date[] = [];
    const startDate = new Date(year, 0, 1);
    const endDate = new Date(year, 11, 31);
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d));
    }
    
    return dates;
  }, []);

  // Pre-index invoice net totals by event date once per invoices change, so the
  // per-row lookup in scheduleData is O(1) instead of O(invoices) × 365 rows.
  const invoiceTotalByDate = useMemo(() => {
    const m = new Map<string, number>();
    for (const inv of invoices) {
      if (!inv.dateOfEvent) continue;
      // Task #112 — dateOfEvent is a canonical YYYY-MM-DD string.
      const raw = inv.dateOfEvent as unknown as string;
      const showDate = typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw)
        ? raw.slice(0, 10)
        : new Date(raw).toISOString().split('T')[0];
      const net = Math.max(0, parseFloat(inv.eventRate || "0") - parseFloat(inv.discount || "0"));
      m.set(showDate, (m.get(showDate) || 0) + net);
    }
    return m;
  }, [invoices]);

  const getInvoiceTotalForDate = useCallback((date: Date): number => {
    const dateStr = date.toISOString().split('T')[0];
    return invoiceTotalByDate.get(dateStr) || 0;
  }, [invoiceTotalByDate]);

  const scheduleData = useMemo((): ScheduleRowData[] => {
    const yearDates = generateYearDates(selectedYear);
    
    return yearDates.map(date => {
      const dateStr = date.toISOString().split('T')[0];
      const entry = scheduleEntries.find(e => {
        const entryDate = new Date(e.date).toISOString().split('T')[0];
        return entryDate === dateStr;
      });
      
      return {
        date,
        dayOfWeek: DAYS_OF_WEEK[date.getDay()],
        entry,
        invoiceTotal: getInvoiceTotalForDate(date),
      };
    });
  }, [selectedYear, scheduleEntries, generateYearDates, getInvoiceTotalForDate]);

  // Compute which dateKey should show the run label per run (viewport-aware anchoring)
  // When the first row of a run scrolls above the visible area, the label moves to the
  // topmost visible row of that run so users always see the run context.
  // NOTE: must be declared after `scheduleData` to avoid a TDZ ReferenceError
  // ("Cannot access 'scheduleData' before initialization") when this useMemo
  // runs during the first render pass.
  const runLabelDateKey = useMemo(() => {
    const ROW_HEIGHT = 24;
    const labelMap = new Map<string, string>(); // runId → dateKey that shows label
    // Group dateKeys per runId
    const runDates = new Map<string, string[]>();
    for (const [dk, info] of runsByDate) {
      if (!runDates.has(info.runId)) runDates.set(info.runId, []);
      runDates.get(info.runId)!.push(dk);
    }
    // Header row offset approximation (one sticky header row)
    const HEADER_HEIGHT = 28;
    const firstVisibleIdx = Math.max(0, Math.floor((tableScrollTop - HEADER_HEIGHT) / ROW_HEIGHT));
    for (const [runId, dates] of runDates) {
      const sorted = dates.sort();
      const firstRunIdx = scheduleData.findIndex((r) => r.date.toISOString().slice(0, 10) === sorted[0]);
      if (firstRunIdx < 0) continue;
      const labelIdx = Math.max(firstRunIdx, firstVisibleIdx);
      // Walk forward to the first run date at or after labelIdx
      for (let i = labelIdx; i < scheduleData.length; i++) {
        const dk = scheduleData[i].date.toISOString().slice(0, 10);
        if (dates.includes(dk)) { labelMap.set(runId, dk); break; }
      }
    }
    return labelMap;
  }, [runsByDate, tableScrollTop, scheduleData]);

  const calculateEstDays = (leadPages: number | null | undefined): number => {
    if (!leadPages) return 0;
    return leadPages * 20 * 0.8 * 0.2 * 0.66 / 4;
  };

  // Task #289 — Returns the effective (sandbox-overlaid) entry fields for a row.
  // Uses snapshot as base so live query refetches don't mutate in-flight edits.
  // Deeply merges customFields so unedited custom values are preserved.
  const getSandboxEntry = useCallback((row: ScheduleRowData): Record<string, any> => {
    const dateKey = row.date.toISOString().slice(0, 10);
    // In sandbox mode, use the snapshot as the authoritative base to prevent drift.
    // However, if the snapshot captured this date as empty (entry === undefined) and a live
    // entry now exists (e.g., a Tour AI draft created after sandbox opened), fall back to
    // the live entry so AI-generated draft values are visible in the sandbox grid.
    const snapRow = sandboxMode && sandboxSnapshot
      ? sandboxSnapshot.find(r => r.date.toISOString().slice(0, 10) === dateKey)
      : undefined;
    const baseEntry = snapRow?.entry != null ? snapRow.entry : row.entry;
    const base: Record<string, any> = baseEntry ? { ...baseEntry } : {};
    if (!sandboxMode) return base;
    const overlay = sandboxEntries[dateKey];
    if (!overlay) return base;
    const merged: Record<string, any> = { ...base, ...overlay };
    // Deep-merge customFields so sandbox edits don't shadow unedited custom columns
    if (overlay.customFields) {
      merged.customFields = {
        ...((base.customFields as Record<string, any>) ?? {}),
        ...(overlay.customFields as Record<string, any>),
      };
    }
    return merged;
  }, [sandboxMode, sandboxEntries, sandboxSnapshot]);

  // Task #289 — Open sandbox mode: deep-clone entries to freeze snapshot
  const openSandbox = useCallback(() => {
    // Deep-clone entry objects so subsequent live query refetches don't mutate snapshot values
    setSandboxSnapshot(scheduleData.map(row => ({
      ...row,
      entry: row.entry ? { ...(row.entry as object) } as typeof row.entry : undefined,
    })));
    setSandboxEntries({});
    setSandboxMode(true);
  }, [scheduleData]);

  const discardDraft = useMutation({
    mutationFn: async (draftGroupId: string) => {
      const resp = await apiRequest("POST", `/api/schedule/draft/${draftGroupId}/discard`, {});
      return resp.json();
    },
    onSuccess: () => {
      setActiveDraftGroupId(undefined);
      queryClient.invalidateQueries({ queryKey: ["/api/schedule", selectedYear] });
      toast({ title: "Draft discarded" });
    },
    onError: () => toast({ title: "Error", description: "Failed to discard draft.", variant: "destructive" }),
  });

  // Commit draft entries — two-step: preview for new regions, then confirm.
  // NOTE: these `useMutation` calls MUST live above the `if (entriesLoading)
  // early return below — otherwise when the schedule query finishes loading
  // and the component re-renders, the hook count changes and React throws
  // "Rendered fewer hooks than expected", crashing the master-schedule page.
  // They must also live above `publishSandbox` (which references `commitDraft`
  // in its body and dependency array) to avoid a temporal-dead-zone crash
  // ("Cannot access 'commitDraft' before initialization").
  const previewDraft = useMutation({
    mutationFn: async (draftGroupId: string) => {
      const resp = await apiRequest("POST", `/api/schedule/draft/${draftGroupId}/commit`, { preview: true });
      return resp.json() as Promise<{ newRegions: string[] }>;
    },
    onSuccess: (data, draftGroupId) => {
      if (data.newRegions && data.newRegions.length > 0) {
        setPendingRegions(data.newRegions);
        setPendingCommitId(draftGroupId);
      } else {
        commitDraft.mutate({ draftGroupId, createRegions: false });
      }
    },
    onError: () => toast({ title: "Error", description: "Failed to preview draft.", variant: "destructive" }),
  });

  const commitDraft = useMutation({
    mutationFn: async ({ draftGroupId, createRegions }: { draftGroupId: string; createRegions: boolean }) => {
      const resp = await apiRequest("POST", `/api/schedule/draft/${draftGroupId}/commit`, { createRegions });
      return resp.json();
    },
    onSuccess: () => {
      setActiveDraftGroupId(undefined);
      setPendingRegions(null);
      setPendingCommitId(null);
      queryClient.invalidateQueries({ queryKey: ["/api/schedule", selectedYear] });
      queryClient.invalidateQueries({ queryKey: ["/api/schedule/runs", selectedYear] });
      queryClient.invalidateQueries({ queryKey: ["/api/regions"] });
      toast({ title: "Draft saved", description: "Schedule entries committed successfully." });
    },
    onError: () => toast({ title: "Error", description: "Failed to commit draft.", variant: "destructive" }),
  });

  // Task #289 — Discard sandbox (and AI draft if any)
  // draftId: pass activeDraftId from the render body so we always catch DB-sourced drafts
  const discardSandboxAndDraft = useCallback((draftId?: string) => {
    if (draftId) {
      discardDraft.mutate(draftId);
    }
    setSandboxMode(false);
    setSandboxEntries({});
    setSandboxSnapshot(null);
  }, [discardDraft]);

  // Task #289 — Publish sandbox: apply sandboxEntries diff + commit AI draft.
  // Merges overlay customFields with current live entry to preserve unedited custom columns.
  const publishSandbox = useCallback(async (activeDraftId?: string) => {
    setIsSandboxPublishing(true);
    try {
      await Promise.all(
        Object.entries(sandboxEntries).map(async ([dateKey, overlay]) => {
          // Prefer live row for entry ID (it may have been created since sandbox opened),
          // fall back to snapshot for rows that no longer exist live
          const liveRow = scheduleData.find(r => r.date.toISOString().slice(0, 10) === dateKey);
          const snapRow = (sandboxSnapshot ?? []).find(r => r.date.toISOString().slice(0, 10) === dateKey);
          const sourceRow = liveRow ?? snapRow;

          // Merge customFields with the live entry so we don't overwrite unedited custom columns
          const mergedOverlay: Record<string, any> = { ...overlay };
          if (overlay.customFields) {
            const liveCustomFields = (liveRow?.entry as any)?.customFields ?? {};
            mergedOverlay.customFields = {
              ...liveCustomFields,
              ...(overlay.customFields as Record<string, any>),
            };
          }

          if (sourceRow?.entry) {
            await updateEntryMutation.mutateAsync({ id: sourceRow.entry.id, updates: mergedOverlay });
          } else {
            const date = new Date(dateKey + "T12:00:00");
            await createEntryMutation.mutateAsync({ year: selectedYear, date, ...mergedOverlay });
          }
        })
      );
      if (activeDraftId) {
        await commitDraft.mutateAsync({ draftGroupId: activeDraftId, createRegions: false });
        setActiveDraftGroupId(undefined);
      }
      queryClient.invalidateQueries({ queryKey: ["/api/schedule", selectedYear, selectedTalentId] });
      queryClient.invalidateQueries({ queryKey: ["/api/schedule/runs", selectedYear] });
      setSandboxMode(false);
      setSandboxEntries({});
      setSandboxSnapshot(null);
      toast({ title: "Published", description: "Sandbox changes saved to live schedule." });
    } catch {
      toast({ title: "Error", description: "Failed to publish sandbox.", variant: "destructive" });
    }
    setIsSandboxPublishing(false);
  }, [sandboxEntries, sandboxSnapshot, scheduleData, selectedYear, selectedTalentId,
      updateEntryMutation, createEntryMutation, commitDraft, queryClient, toast]);

  const entriesMap = useMemo(() => {
    const map = new Map<string, ScheduleEntry>();
    scheduleEntries.forEach(entry => {
      if (entry.date) {
        const dateStr = new Date(entry.date).toISOString().split('T')[0];
        map.set(dateStr, entry);
      }
    });
    return map;
  }, [scheduleEntries]);

  const calendarDays = useMemo((): CalendarDayData[] => {
    const firstDay = new Date(selectedYear, selectedMonth, 1);
    const lastDay = new Date(selectedYear, selectedMonth + 1, 0);
    const startDayOfWeek = firstDay.getDay();
    const days: CalendarDayData[] = [];

    for (let i = 0; i < startDayOfWeek; i++) {
      const prevDate = new Date(selectedYear, selectedMonth, -startDayOfWeek + i + 1);
      const dateStr = prevDate.toISOString().split('T')[0];
      days.push({
        date: prevDate,
        entry: entriesMap.get(dateStr),
        invoiceTotal: getInvoiceTotalForDate(prevDate),
        isCurrentMonth: false,
        isToday: false,
      });
    }

    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(selectedYear, selectedMonth, d);
      const dateStr = date.toISOString().split('T')[0];
      const isToday = date.toDateString() === today.toDateString();
      days.push({
        date,
        entry: entriesMap.get(dateStr),
        invoiceTotal: getInvoiceTotalForDate(date),
        isCurrentMonth: true,
        isToday,
      });
    }

    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const nextDate = new Date(selectedYear, selectedMonth + 1, i);
      const dateStr = nextDate.toISOString().split('T')[0];
      days.push({
        date: nextDate,
        entry: entriesMap.get(dateStr),
        invoiceTotal: getInvoiceTotalForDate(nextDate),
        isCurrentMonth: false,
        isToday: false,
      });
    }

    return days;
  }, [selectedYear, selectedMonth, entriesMap, getInvoiceTotalForDate]);

  const upcomingShows = useMemo(() => {
    const todayStr = today.toISOString().split('T')[0];
    return scheduleEntries
      .filter(entry => {
        if (!entry.date || !entry.status) return false;
        const entryDate = new Date(entry.date).toISOString().split('T')[0];
        return entryDate >= todayStr && 
          (entry.status === "Public Show" || entry.status === "SL Shows" || entry.status === "Outside Show");
      })
      .sort((a, b) => new Date(a.date!).getTime() - new Date(b.date!).getTime())
      .slice(0, 10);
  }, [scheduleEntries]);

  // Get schedule entries for selected month (for list view)
  const monthScheduleEntries = useMemo(() => {
    const firstOfMonth = new Date(selectedYear, selectedMonth, 1);
    const lastOfMonth = new Date(selectedYear, selectedMonth + 1, 0);
    
    return scheduleEntries
      .filter(entry => {
        if (!entry.date) return false;
        const entryDate = new Date(entry.date);
        return entryDate >= firstOfMonth && entryDate <= lastOfMonth;
      })
      .sort((a, b) => new Date(a.date!).getTime() - new Date(b.date!).getTime());
  }, [scheduleEntries, selectedYear, selectedMonth]);

  const monthStats = useMemo(() => {
    const shows = calendarDays.filter(d => 
      d.isCurrentMonth && d.entry?.status && 
      (d.entry.status === "Public Show" || d.entry.status === "SL Shows" || d.entry.status === "Outside Show")
    ).length;
    const travelDays = calendarDays.filter(d => 
      d.isCurrentMonth && d.entry?.status === "Travel Day"
    ).length;
    const daysOff = calendarDays.filter(d => 
      d.isCurrentMonth && d.entry?.status === "Day Off"
    ).length;
    const totalRevenue = calendarDays
      .filter(d => d.isCurrentMonth)
      .reduce((sum, d) => sum + d.invoiceTotal, 0);
    
    return { shows, travelDays, daysOff, totalRevenue };
  }, [calendarDays]);

  const getStatusColor = (status: string | null | undefined) => {
    switch (status) {
      case "Public Show":
        return "bg-green-500 dark:bg-green-600";
      case "SL Shows":
        return "bg-pink-500 dark:bg-pink-600";
      case "Travel Day":
        return "bg-amber-500 dark:bg-amber-600";
      case "Day Off":
        return "bg-orange-400 dark:bg-orange-500";
      case "Outside Show":
        return "bg-blue-500 dark:bg-blue-600";
      default:
        return "";
    }
  };

  const getStatusBgClass = (status: string | null | undefined) => {
    switch (status) {
      case "Public Show":
        return "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800";
      case "SL Shows":
        return "bg-pink-50 dark:bg-pink-900/20 border-pink-200 dark:border-pink-800";
      case "Travel Day":
        return "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800";
      case "Day Off":
        return "bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800";
      case "Outside Show":
        return "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800";
      default:
        return "";
    }
  };

  const goToPrevMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const goToToday = () => {
    setSelectedYear(today.getFullYear());
    setSelectedMonth(today.getMonth());
  };

  const currentMonth = useMemo(() => {
    return new Date(selectedYear, selectedMonth, 1);
  }, [selectedYear, selectedMonth]);

  const currentMonthLabel = useMemo(() => {
    return `${MONTHS[selectedMonth]} ${selectedYear}`;
  }, [selectedMonth, selectedYear]);

  const getScheduleEntryForDate = useCallback((date: Date): ScheduleEntry | undefined => {
    const dateStr = date.toISOString().split('T')[0];
    return entriesMap.get(dateStr);
  }, [entriesMap]);

  const currentMonthStats = useMemo(() => {
    const stats: Record<string, number> = {};
    calendarDays
      .filter(d => d.isCurrentMonth)
      .forEach(d => {
        const status = d.entry?.status || "Unscheduled";
        stats[status] = (stats[status] || 0) + 1;
      });
    return stats;
  }, [calendarDays]);

  const formatDate = (date: Date): string => {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear().toString().slice(-2);
    return `${month}/${day}/${year}`;
  };

  const handleCellEdit = (dateStr: string, field: string, currentValue: string | null | undefined) => {
    setEditingCell({ date: dateStr, field });
    setEditValue(currentValue || "");
  };

  const openNotesDialog = (row: ScheduleRowData) => {
    setNotesDialogRow(row);
    // Task #289 — pre-fill from sandbox overlay if active, so user sees overlaid value
    const effEntry = getSandboxEntry(row);
    setNotesDialogValue((effEntry.notes as string | undefined) || "");
    setNotesDialogOpen(true);
  };

  const handleNotesSave = async () => {
    if (!notesDialogRow) return;
    // Task #289 — Sandbox mode: write to overlay instead of DB
    if (sandboxMode) {
      const dateKey = notesDialogRow.date.toISOString().slice(0, 10);
      setSandboxEntries(prev => ({
        ...prev,
        [dateKey]: { ...(prev[dateKey] ?? {}), notes: notesDialogValue },
      }));
      setNotesDialogOpen(false);
      setNotesDialogRow(null);
      setNotesDialogValue("");
      return;
    }
    const { entry, date } = notesDialogRow;
    if (entry) {
      await updateEntryMutation.mutateAsync({ id: entry.id, updates: { notes: notesDialogValue } });
    } else {
      await createEntryMutation.mutateAsync({ year: selectedYear, date, notes: notesDialogValue });
    }
    setNotesDialogOpen(false);
    setNotesDialogRow(null);
    setNotesDialogValue("");
  };

  // Core DB column keys — these map directly to schedule_entries columns.
  // Anything not in this set is a generic workspace custom column stored in
  // the custom_fields JSONB column.
  const CORE_ENTRY_FIELDS = new Set([
    "status", "city", "state", "regionId", "optionB", "notes", "driveTime", "leadPages",
  ]);

  const handleCellSave = async (row: ScheduleRowData, field: string) => {
    const { entry, date } = row;
    
    let processedValue: any = editValue;
    if (field === "leadPages") {
      processedValue = editValue ? parseInt(editValue) : null;
    }

    // Task #289 — Sandbox mode: write to overlay instead of DB.
    // Core fields are stored at top-level; custom columns under customFields (matching live write shape).
    if (sandboxMode) {
      const dateKey = row.date.toISOString().slice(0, 10);
      setSandboxEntries(prev => {
        const existing = prev[dateKey] ?? {};
        if (CORE_ENTRY_FIELDS.has(field)) {
          return { ...prev, [dateKey]: { ...existing, [field]: processedValue } };
        } else {
          const existingCustom = (existing.customFields as Record<string, any>) ?? {};
          return { ...prev, [dateKey]: { ...existing, customFields: { ...existingCustom, [field]: processedValue } } };
        }
      });
      setEditingCell(null);
      setEditValue("");
      return;
    }

    // Generic workspace custom columns → merge into customFields JSONB
    const updates: Record<string, any> = CORE_ENTRY_FIELDS.has(field)
      ? { [field]: processedValue }
      : { customFields: { ...((entry as any)?.customFields ?? {}), [field]: processedValue } };
    
    let savedEntry: any;
    if (entry) {
      savedEntry = await updateEntryMutation.mutateAsync({ id: entry.id, updates });
    } else {
      savedEntry = await createEntryMutation.mutateAsync({
        year: selectedYear,
        date,
        ...updates,
      });
    }
    
    setEditingCell(null);
    setEditValue("");

    // Drive-time auto-calculation: trigger after city or state is saved (Task #59, Step 11)
    if (field === "city" || field === "state") {
      const rowDate = date instanceof Date ? date.toISOString().slice(0, 10) : String(date).slice(0, 10);
      // Get updated city/state (may have just been written)
      const newCity = field === "city" ? processedValue : (entry?.city ?? "");
      const newState = field === "state" ? processedValue : (entry?.state ?? "");
      if (newCity && newState) {
        // Find the previous day's entry for drive-time origin
        const currentIdx = scheduleData.findIndex(r => {
          const d = r.date instanceof Date ? r.date.toISOString().slice(0, 10) : String(r.date).slice(0, 10);
          return d === rowDate;
        });
        const prevRow = currentIdx > 0 ? scheduleData[currentIdx - 1] : null;
        const prevCity = prevRow?.entry?.city;
        const prevState = prevRow?.entry?.state;
        if (prevCity && prevState && (prevCity !== newCity || prevState !== newState)) {
          try {
            const resp = await apiRequest("POST", "/api/schedule/drive-time", {
              fromCity: prevCity, fromState: prevState,
              toCity: newCity, toState: newState,
            });
            const data = await resp.json();
            if (data.driveTime && (entry || savedEntry)) {
              const entryId = entry?.id ?? savedEntry?.id;
              if (entryId) {
                // _fieldSourceOverride="ai" → PATCH route marks driveTime source as "ai"
                // so future AI tool writes are not blocked by user-ownership check.
                await updateEntryMutation.mutateAsync({ id: entryId, updates: { driveTime: data.driveTime, _fieldSourceOverride: "ai" } as any });
              }
            }
          } catch {
            // Drive time calculation is best-effort; silently ignore failures
          }
        }
      }
    }
  };

  // Task #289 — Keyboard navigation and copy/paste for cell selection.
  // Selectable fields are derived from mergedColumns so custom columns participate automatically.
  const CORE_ENTRY_FIELDS_KEYS = new Set(["status", "city", "state", "regionId", "optionB", "notes", "driveTime", "leadPages"]);
  const NON_SELECTABLE_COLS = new Set(["date", "day", "invoiceTotal", "estDays"]);

  // Selectable field list at component scope — shared by keyboard handler and range-selection useMemo
  const selectableFieldsList = useMemo(
    () => mergedColumns.filter(col => !NON_SELECTABLE_COLS.has(col.key)).map(col => col.key),
    [mergedColumns]
  );

  // Compute the full set of "dateKey:field" keys in the current selection rectangle.
  // Single-cell selection = Set with one entry. Multi-cell = rectangle from anchor→end.
  const selectedCellKeys = useMemo<Set<string>>(() => {
    if (!selectedCell) return new Set();
    const end = selectionEnd ?? selectedCell;
    if (end.dateKey === selectedCell.dateKey && end.field === selectedCell.field) {
      return new Set([`${selectedCell.dateKey}:${selectedCell.field}`]);
    }
    const aRow = scheduleData.findIndex(r => r.date.toISOString().slice(0, 10) === selectedCell.dateKey);
    const eRow = scheduleData.findIndex(r => r.date.toISOString().slice(0, 10) === end.dateKey);
    const aCol = selectableFieldsList.indexOf(selectedCell.field);
    const eCol = selectableFieldsList.indexOf(end.field);
    const minRow = Math.min(aRow, eRow); const maxRow = Math.max(aRow, eRow);
    const minCol = Math.min(Math.max(aCol, 0), Math.max(eCol, 0));
    const maxCol = Math.max(Math.max(aCol, 0), Math.max(eCol, 0));
    const keys = new Set<string>();
    for (let r = minRow; r <= maxRow; r++) {
      const dk = scheduleData[r]?.date.toISOString().slice(0, 10);
      if (!dk) continue;
      for (let c = minCol; c <= maxCol; c++) {
        const fk = selectableFieldsList[c];
        if (fk) keys.add(`${dk}:${fk}`);
      }
    }
    return keys;
  }, [selectedCell, selectionEnd, scheduleData, selectableFieldsList]);

  useEffect(() => {
    if (viewMode !== "spreadsheet") return;
    // Derive selectable column keys from the actual rendered column list (includes custom columns)
    const SELECTABLE_FIELDS = mergedColumns
      .filter(col => !NON_SELECTABLE_COLS.has(col.key))
      .map(col => col.key);

    // Resolve a cell's display value, respecting core vs customFields shape
    const getCellEffValue = (eff: Record<string, any>, field: string): string => {
      if (CORE_ENTRY_FIELDS_KEYS.has(field)) {
        return String(eff[field] ?? "");
      }
      return String((eff.customFields as Record<string, any> | undefined)?.[field] ?? "");
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (editingCell) return;
      const tag = (e.target as HTMLElement)?.tagName?.toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (!selectedCell) return;

      const idx = scheduleData.findIndex(r => r.date.toISOString().slice(0, 10) === selectedCell.dateKey);
      const fi = SELECTABLE_FIELDS.indexOf(selectedCell.field);

      // Shift+Arrow: extend the selection rectangle (anchor stays, end moves)
      if (e.shiftKey && ["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(e.key)) {
        e.preventDefault();
        const curEnd = selectionEnd ?? selectedCell;
        const eIdx = scheduleData.findIndex(r => r.date.toISOString().slice(0, 10) === curEnd.dateKey);
        const eFi = SELECTABLE_FIELDS.indexOf(curEnd.field);
        if (e.key === "ArrowDown" && eIdx < scheduleData.length - 1) {
          setSelectionEnd({ dateKey: scheduleData[eIdx + 1].date.toISOString().slice(0, 10), field: curEnd.field });
        } else if (e.key === "ArrowUp" && eIdx > 0) {
          setSelectionEnd({ dateKey: scheduleData[eIdx - 1].date.toISOString().slice(0, 10), field: curEnd.field });
        } else if (e.key === "ArrowRight" && eFi < SELECTABLE_FIELDS.length - 1) {
          setSelectionEnd({ dateKey: curEnd.dateKey, field: SELECTABLE_FIELDS[eFi + 1] });
        } else if (e.key === "ArrowLeft" && eFi > 0) {
          setSelectionEnd({ dateKey: curEnd.dateKey, field: SELECTABLE_FIELDS[eFi - 1] });
        }
        return;
      }

      if (e.key === "ArrowDown" && idx < scheduleData.length - 1) {
        e.preventDefault();
        setSelectedCell({ dateKey: scheduleData[idx + 1].date.toISOString().slice(0, 10), field: selectedCell.field });
        setSelectionEnd(null);
      } else if (e.key === "ArrowUp" && idx > 0) {
        e.preventDefault();
        setSelectedCell({ dateKey: scheduleData[idx - 1].date.toISOString().slice(0, 10), field: selectedCell.field });
        setSelectionEnd(null);
      } else if (e.key === "ArrowRight" && fi < SELECTABLE_FIELDS.length - 1) {
        e.preventDefault();
        setSelectedCell({ dateKey: selectedCell.dateKey, field: SELECTABLE_FIELDS[fi + 1] });
        setSelectionEnd(null);
      } else if (e.key === "ArrowLeft" && fi > 0) {
        e.preventDefault();
        setSelectedCell({ dateKey: selectedCell.dateKey, field: SELECTABLE_FIELDS[fi - 1] });
        setSelectionEnd(null);
      } else if ((e.metaKey || e.ctrlKey) && e.key === "c") {
        if (idx === -1) return;
        const row = scheduleData[idx];
        const eff = getSandboxEntry(row);
        const val = getCellEffValue(eff, selectedCell.field);
        navigator.clipboard.writeText(val).catch(() => {});
        toast({ title: "Copied" });
      } else if ((e.metaKey || e.ctrlKey) && e.key === "v") {
        e.preventDefault();
        if (idx === -1) return;
        const row = scheduleData[idx];
        navigator.clipboard.readText().then(text => {
          const dateKey = row.date.toISOString().slice(0, 10);
          const field = selectedCell.field;
          if (sandboxMode) {
            let processedValue: any = text;
            if (field === "leadPages") processedValue = text ? parseInt(text) : null;
            setSandboxEntries(prev => {
              const existing = prev[dateKey] ?? {};
              if (CORE_ENTRY_FIELDS_KEYS.has(field)) {
                return { ...prev, [dateKey]: { ...existing, [field]: processedValue } };
              } else {
                const existingCustom = (existing.customFields as Record<string, any>) ?? {};
                return { ...prev, [dateKey]: { ...existing, customFields: { ...existingCustom, [field]: processedValue } } };
              }
            });
          } else {
            handlePaste(row, field);
          }
        }).catch(() => {});
      } else if (e.key === "Delete" || e.key === "Backspace") {
        // Clear the value of every selected cell (skip non-clearable fields silently)
        e.preventDefault();
        const NON_CLEARABLE = new Set(["date", "day", "invoiceTotal", "status", "region", "regionId"]);
        if (sandboxMode) {
          setSandboxEntries(prev => {
            const next = { ...prev };
            for (const cellKey of selectedCellKeys) {
              const colonIdx = cellKey.indexOf(":");
              const dateKey = cellKey.slice(0, colonIdx);
              const field = cellKey.slice(colonIdx + 1);
              if (NON_CLEARABLE.has(field)) continue;
              const existing = next[dateKey] ?? {};
              if (CORE_ENTRY_FIELDS_KEYS.has(field)) {
                next[dateKey] = { ...existing, [field]: null };
              } else {
                const existingCustom = (existing.customFields as Record<string, any>) ?? {};
                next[dateKey] = { ...existing, customFields: { ...existingCustom, [field]: null } };
              }
            }
            return next;
          });
        } else {
          // Group clearable fields by dateKey so we issue exactly one mutation per row,
          // avoiding last-write-wins races when multiple cells in the same row are selected.
          const rowUpdates = new Map<string, { coreClears: Record<string, null>; customClears: string[] }>();
          for (const cellKey of selectedCellKeys) {
            const colonIdx = cellKey.indexOf(":");
            const dateKey = cellKey.slice(0, colonIdx);
            const field = cellKey.slice(colonIdx + 1);
            if (NON_CLEARABLE.has(field)) continue;
            if (!rowUpdates.has(dateKey)) rowUpdates.set(dateKey, { coreClears: {}, customClears: [] });
            const bucket = rowUpdates.get(dateKey)!;
            if (CORE_ENTRY_FIELDS_KEYS.has(field)) {
              bucket.coreClears[field] = null;
            } else {
              bucket.customClears.push(field);
            }
          }
          // Separate rows that already have an entry (bulk via one round-trip) from
          // rows that need a new entry created (rare — individual creates).
          const bulkUpdates: Array<{ id: string; updates: Record<string, any> }> = [];
          const creates: Promise<any>[] = [];
          for (const [dateKey, { coreClears, customClears }] of rowUpdates) {
            const row = scheduleData.find(r => r.date.toISOString().slice(0, 10) === dateKey);
            if (!row) continue;
            const updates: Record<string, any> = { ...coreClears };
            if (customClears.length > 0) {
              const eff = getSandboxEntry(row);
              const existingCustom = (eff.customFields as Record<string, any>) ?? {};
              const clearedCustom = { ...existingCustom };
              for (const f of customClears) clearedCustom[f] = null;
              updates.customFields = clearedCustom;
            }
            if (row.entry) {
              bulkUpdates.push({ id: row.entry.id, updates });
            } else {
              creates.push(createEntryMutation.mutateAsync({ year: selectedYear, date: row.date, ...updates }));
            }
          }
          if (bulkUpdates.length > 0) {
            bulkUpdateEntryMutation.mutateAsync(bulkUpdates).catch(() => {});
          }
          if (creates.length > 0) {
            Promise.all(creates).catch(() => {});
          }
        }
      } else if (e.key === "Tab" && idx !== -1) {
        // Tab / Shift+Tab advances selectedCell left/right (wrapping to next/prev row)
        e.preventDefault();
        const forward = !e.shiftKey;
        if (forward) {
          if (fi < SELECTABLE_FIELDS.length - 1) {
            setSelectedCell({ dateKey: selectedCell.dateKey, field: SELECTABLE_FIELDS[fi + 1] });
          } else if (idx < scheduleData.length - 1) {
            setSelectedCell({ dateKey: scheduleData[idx + 1].date.toISOString().slice(0, 10), field: SELECTABLE_FIELDS[0] });
          }
        } else {
          if (fi > 0) {
            setSelectedCell({ dateKey: selectedCell.dateKey, field: SELECTABLE_FIELDS[fi - 1] });
          } else if (idx > 0) {
            setSelectedCell({ dateKey: scheduleData[idx - 1].date.toISOString().slice(0, 10), field: SELECTABLE_FIELDS[SELECTABLE_FIELDS.length - 1] });
          }
        }
      } else if (e.key === "Escape") {
        setSelectedCell(null);
      } else if ((e.key === "Enter" || e.key === "F2") && idx !== -1) {
        // status, region, regionId are Select/Popover widgets — not EditableCell;
        // exclude them from the inline-editor open path
        const nonEditable = new Set(["date", "day", "invoiceTotal", "status", "region", "regionId"]);
        if (!nonEditable.has(selectedCell.field)) {
          const row = scheduleData[idx];
          const eff = getSandboxEntry(row);
          setEditingCell({ date: row.date.toISOString(), field: selectedCell.field });
          setEditValue(getCellEffValue(eff, selectedCell.field));
        }
      } else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey && idx !== -1) {
        const nonEditable = new Set(["date", "day", "invoiceTotal", "status", "region", "regionId"]);
        if (!nonEditable.has(selectedCell.field)) {
          const row = scheduleData[idx];
          setEditingCell({ date: row.date.toISOString(), field: selectedCell.field });
          setEditValue(e.key);
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [viewMode, selectedCell, selectionEnd, editingCell, scheduleData, getSandboxEntry, sandboxMode, setSandboxEntries, toast, mergedColumns, selectedCellKeys, updateEntryMutation, bulkUpdateEntryMutation, createEntryMutation, selectedYear]);

  // Stop selection drag on global mouseup
  useEffect(() => {
    const stop = () => setIsSelectDragging(false);
    document.addEventListener("mouseup", stop);
    return () => document.removeEventListener("mouseup", stop);
  }, []);

  // Task #289 — Fill handle drag: apply value to range on mouseup
  useEffect(() => {
    if (!fillDrag) return;
    const handleMouseUp = () => {
      if (fillDrag && fillDragEndKey && fillDragEndKey !== fillDrag.startKey) {
        const startIdx = scheduleData.findIndex(r => r.date.toISOString().slice(0, 10) === fillDrag.startKey);
        const endIdx = scheduleData.findIndex(r => r.date.toISOString().slice(0, 10) === fillDragEndKey);
        if (startIdx !== -1 && endIdx !== -1) {
          const [from, to] = startIdx <= endIdx ? [startIdx + 1, endIdx] : [endIdx, startIdx - 1];
          const field = fillDrag.field;
          const value = fillDrag.value;
          let processedValue: any = value;
          if (field === "leadPages") processedValue = value ? parseInt(value) : null;
          if (sandboxMode) {
            for (let i = from; i <= to; i++) {
              const row = scheduleData[i];
              const dateKey = row.date.toISOString().slice(0, 10);
              setSandboxEntries(prev => {
                const existing = prev[dateKey] ?? {};
                if (CORE_ENTRY_FIELDS.has(field)) {
                  return { ...prev, [dateKey]: { ...existing, [field]: processedValue } };
                } else {
                  const existingCustom = (existing.customFields as Record<string, any>) ?? {};
                  return { ...prev, [dateKey]: { ...existing, customFields: { ...existingCustom, [field]: processedValue } } };
                }
              });
            }
          } else {
            // Batch rows that have existing entries into one round-trip; create the rest individually.
            const bulkUpdates: Array<{ id: string; updates: Record<string, any> }> = [];
            const creates: Array<() => void> = [];
            for (let i = from; i <= to; i++) {
              const row = scheduleData[i];
              const updates: Record<string, any> = CORE_ENTRY_FIELDS.has(field)
                ? { [field]: processedValue }
                : { customFields: { ...((row.entry as any)?.customFields ?? {}), [field]: processedValue } };
              if (row.entry) {
                bulkUpdates.push({ id: row.entry.id, updates });
              } else {
                creates.push(() => createEntryMutation.mutate({ year: selectedYear, date: row.date, ...updates }));
              }
            }
            if (bulkUpdates.length > 0) {
              bulkUpdateEntryMutation.mutate(bulkUpdates);
            }
            for (const fn of creates) fn();
          }
        }
      }
      setFillDrag(null);
      setFillDragEndKey(null);
    };
    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, [fillDrag, fillDragEndKey, scheduleData, sandboxMode, selectedYear, updateEntryMutation, bulkUpdateEntryMutation, createEntryMutation]);

  // Navigate to next/previous cell on Tab/Shift+Tab
  const handleTabNavigation = useCallback((currentDate: string, currentField: string, shiftKey: boolean) => {
    const currentFieldIndex = EDITABLE_FIELDS.indexOf(currentField);
    const currentRowIndex = scheduleData.findIndex(row => row.date.toISOString().split('T')[0] === currentDate);
    
    if (currentFieldIndex === -1 || currentRowIndex === -1) return;
    
    let nextFieldIndex = currentFieldIndex;
    let nextRowIndex = currentRowIndex;
    
    if (shiftKey) {
      // Move backwards
      nextFieldIndex--;
      if (nextFieldIndex < 0) {
        nextFieldIndex = EDITABLE_FIELDS.length - 1;
        nextRowIndex--;
      }
    } else {
      // Move forwards
      nextFieldIndex++;
      if (nextFieldIndex >= EDITABLE_FIELDS.length) {
        nextFieldIndex = 0;
        nextRowIndex++;
      }
    }
    
    // Check bounds
    if (nextRowIndex < 0 || nextRowIndex >= scheduleData.length) {
      setEditingCell(null);
      setEditValue("");
      return;
    }
    
    const nextRow = scheduleData[nextRowIndex];
    const nextField = EDITABLE_FIELDS[nextFieldIndex];
    const nextDateStr = nextRow.date.toISOString().split('T')[0];
    
    // Get the current value for the next cell
    const entry = nextRow.entry;
    let nextValue = "";
    if (entry) {
      nextValue = (entry as any)[nextField] || "";
    }
    
    setEditingCell({ date: nextDateStr, field: nextField });
    setEditValue(String(nextValue));
  }, [scheduleData]);

  const handleStatusChange = async (row: ScheduleRowData, newStatus: string) => {
    // Task #289 — Sandbox mode: write to overlay instead of DB
    if (sandboxMode) {
      const dateKey = row.date.toISOString().slice(0, 10);
      setSandboxEntries(prev => ({ ...prev, [dateKey]: { ...(prev[dateKey] ?? {}), status: newStatus } }));
      return;
    }
    const { entry, date } = row;
    if (entry) {
      await updateEntryMutation.mutateAsync({ id: entry.id, updates: { status: newStatus } });
    } else {
      await createEntryMutation.mutateAsync({ year: selectedYear, date, status: newStatus });
    }
  };

  const handleRegionChange = async (row: ScheduleRowData, regionId: string | null) => {
    // Task #289 — Sandbox mode: write to overlay instead of DB
    if (sandboxMode) {
      const dateKey = row.date.toISOString().slice(0, 10);
      setSandboxEntries(prev => ({ ...prev, [dateKey]: { ...(prev[dateKey] ?? {}), regionId } }));
      return;
    }
    const { entry, date } = row;
    if (entry) {
      await updateEntryMutation.mutateAsync({ id: entry.id, updates: { regionId } });
    } else {
      await createEntryMutation.mutateAsync({ year: selectedYear, date, regionId });
    }
  };

  const getRowClassName = (status: string | null | undefined): string => {
    if (status === "Public Show") return "bg-green-100 dark:bg-green-900/30";
    if (status === "SL Shows") return "bg-pink-100 dark:bg-pink-900/30";
    if (status === "Outside Show") return "bg-blue-100 dark:bg-blue-900/30";
    return "";
  };

  const getCellClassName = (status: string | null | undefined): string => {
    if (status === "Day Off") return "bg-orange-200 dark:bg-orange-700/50";
    if (status === "Travel Day") return "bg-yellow-200 dark:bg-yellow-700/50";
    return "";
  };

  // Paste functionality for context menu
  const handlePaste = async (row: ScheduleRowData, field: string) => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        let processedValue: any = text;
        if (field === "leadPages") processedValue = text ? parseInt(text) : null;

        // Task #289 — Sandbox mode: route paste into sandbox overlay, never hit live DB
        if (sandboxMode) {
          const dateKey = row.date.toISOString().slice(0, 10);
          setSandboxEntries(prev => {
            const existing = prev[dateKey] ?? {};
            if (CORE_ENTRY_FIELDS.has(field)) {
              return { ...prev, [dateKey]: { ...existing, [field]: processedValue } };
            } else {
              const existingCustom = (existing.customFields as Record<string, any>) ?? {};
              return { ...prev, [dateKey]: { ...existing, customFields: { ...existingCustom, [field]: processedValue } } };
            }
          });
          toast({ title: "Pasted", description: "Value staged in sandbox." });
          return;
        }

        const { entry, date } = row;
        const updates: Record<string, any> = CORE_ENTRY_FIELDS.has(field)
          ? { [field]: processedValue }
          : { customFields: { ...((entry as any)?.customFields ?? {}), [field]: processedValue } };

        if (entry) {
          await updateEntryMutation.mutateAsync({ id: entry.id, updates });
        } else {
          await createEntryMutation.mutateAsync({ year: selectedYear, date, ...updates });
        }
        toast({ title: "Pasted", description: "Value pasted successfully." });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to paste value.", variant: "destructive" });
    }
  };

  // CSV Export functionality
  const handleExportSchedule = () => {
    if (scheduleData.length === 0) {
      toast({
        title: "No Data",
        description: "There is no schedule data to export.",
        variant: "destructive",
      });
      return;
    }

    // Build CSV headers
    const headers = ["Date", "Day", "Status", "City", "State", "Option B", "Notes", "Drive Time", "Lead Pages", "Est. Days", "Region"];
    
    // Build CSV rows
    const csvRows = [headers.join(",")];
    
    scheduleData.forEach(row => {
      const entry = row.entry;
      const region = regions?.find(r => r.id === entry?.regionId)?.name || "";
      const rowData = [
        row.date.toLocaleDateString("en-US"),
        row.dayOfWeek,
        entry?.status || "",
        entry?.city || "",
        entry?.state || "",
        entry?.optionB || "",
        entry?.notes || "",
        entry?.driveTime?.toString() || "",
        entry?.leadPages?.toString() || "",
        entry?.estDays?.toString() || "",
        region
      ].map(value => {
        const stringValue = String(value || "");
        if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      });
      csvRows.push(rowData.join(","));
    });

    // Download file
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `master_schedule_${selectedYear}_export.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Export Complete",
      description: `Exported ${scheduleData.length} schedule entries to CSV.`,
    });
  };

  // CSV parsing function
  const parseCSV = (text: string): string[][] => {
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentCell = "";
    let insideQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1];

      if (insideQuotes) {
        if (char === '"' && nextChar === '"') {
          currentCell += '"';
          i++;
        } else if (char === '"') {
          insideQuotes = false;
        } else {
          currentCell += char;
        }
      } else {
        if (char === '"') {
          insideQuotes = true;
        } else if (char === ",") {
          currentRow.push(currentCell.trim());
          currentCell = "";
        } else if (char === "\n" || (char === "\r" && nextChar === "\n")) {
          currentRow.push(currentCell.trim());
          if (currentRow.length > 0 && currentRow.some(c => c !== "")) {
            rows.push(currentRow);
          }
          currentRow = [];
          currentCell = "";
          if (char === "\r") i++;
        } else if (char !== "\r") {
          currentCell += char;
        }
      }
    }

    if (currentCell || currentRow.length > 0) {
      currentRow.push(currentCell.trim());
      if (currentRow.some(c => c !== "")) {
        rows.push(currentRow);
      }
    }

    return rows;
  };

  // CSV Import functionality
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        setImportData(text);
        
        const rows = parseCSV(text);
        if (rows.length < 2) {
          toast({ title: "Error", description: "CSV must have a header row and at least one data row.", variant: "destructive" });
          return;
        }
        
        const headers = rows[0];
        const dataRows = rows.slice(1);
        
        setCsvHeaders(headers);
        setCsvDataRows(dataRows);
        
        // Auto-map fields based on header names
        const autoMapping: Record<string, string> = {};
        headers.forEach((header, index) => {
          const normalizedHeader = header.toLowerCase().replace(/[_\s]/g, "");
          
          const matchedField = SCHEDULE_IMPORT_FIELDS.find(f => {
            const normalizedLabel = f.label.toLowerCase().replace(/[_\s]/g, "");
            const normalizedKey = f.key.toLowerCase().replace(/[_\s]/g, "");
            return normalizedLabel === normalizedHeader || normalizedKey === normalizedHeader;
          });
          
          if (matchedField) {
            autoMapping[String(index)] = matchedField.key;
          }
        });
        
        setFieldMapping(autoMapping);
      };
      reader.readAsText(file);
    }
    // Reset file input
    event.target.value = "";
  };

  const handleMappingChange = (csvIndex: string, fieldKey: string) => {
    setFieldMapping(prev => {
      const newMapping = { ...prev };
      if (fieldKey === "__skip__") {
        delete newMapping[csvIndex];
      } else {
        newMapping[csvIndex] = fieldKey;
      }
      return newMapping;
    });
  };

  const closeImportDialog = () => {
    setIsImportDialogOpen(false);
    setImportData("");
    setCsvHeaders([]);
    setCsvDataRows([]);
    setFieldMapping({});
  };

  const parseCSVDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    
    // Try MM/DD/YYYY format
    const mmddyyyy = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mmddyyyy) {
      return new Date(Date.UTC(parseInt(mmddyyyy[3]), parseInt(mmddyyyy[1]) - 1, parseInt(mmddyyyy[2])));
    }
    
    // Try YYYY-MM-DD format
    const yyyymmdd = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (yyyymmdd) {
      return new Date(Date.UTC(parseInt(yyyymmdd[1]), parseInt(yyyymmdd[2]) - 1, parseInt(yyyymmdd[3])));
    }
    
    // Try native Date parsing
    const parsed = new Date(dateStr);
    if (!isNaN(parsed.getTime())) return parsed;
    
    return null;
  };

  const handleImport = async () => {
    if (csvDataRows.length === 0) {
      toast({ title: "Error", description: "No data to import.", variant: "destructive" });
      return;
    }

    // Check if date field is mapped
    const dateColumnIndex = Object.entries(fieldMapping).find(([_, field]) => field === "date")?.[0];
    if (!dateColumnIndex) {
      toast({ title: "Error", description: "You must map a column to 'Date' to import.", variant: "destructive" });
      return;
    }

    setIsImporting(true);
    try {
      // Build all entries for bulk import
      const entries: any[] = [];
      let skipped = 0;

      for (const row of csvDataRows) {
        // Build mapped row from fieldMapping
        const mappedRow: Record<string, string> = {};
        Object.entries(fieldMapping).forEach(([csvIndex, fieldKey]) => {
          const value = row[parseInt(csvIndex)];
          if (value !== undefined && value !== "") {
            mappedRow[fieldKey] = value;
          }
        });

        const dateStr = mappedRow.date;
        const date = parseCSVDate(dateStr);
        
        if (!date) {
          skipped++;
          continue;
        }

        const year = date.getFullYear();
        const entry: any = { year, date: date.toISOString() };
        
        if (mappedRow.status) entry.status = mappedRow.status;
        if (mappedRow.city) entry.city = mappedRow.city;
        if (mappedRow.state) entry.state = mappedRow.state;
        if (mappedRow.optionB) entry.optionB = mappedRow.optionB;
        if (mappedRow.notes) entry.notes = mappedRow.notes;
        if (mappedRow.driveTime) entry.driveTime = mappedRow.driveTime;
        if (mappedRow.leadPages) entry.leadPages = parseInt(mappedRow.leadPages) || null;
        if (mappedRow.optionC) entry.optionC = mappedRow.optionC;
        if (mappedRow.optionD) entry.optionD = mappedRow.optionD;
        if (mappedRow.region) {
          // Pass regionName to backend - it will create the region if it doesn't exist
          entry.regionName = mappedRow.region.trim();
        }

        // Only include entries that have at least one field besides date
        if (Object.keys(entry).length > 2) {
          entries.push(entry);
        } else {
          skipped++;
        }
      }

      if (entries.length === 0) {
        toast({ title: "Error", description: "No valid entries to import.", variant: "destructive" });
        setIsImporting(false);
        return;
      }

      // Use bulk import endpoint for efficient processing
      const response = await apiRequest("POST", "/api/schedule/bulk-import", { entries });
      const result = await response.json();

      queryClient.invalidateQueries({ queryKey: ["/api/schedule", selectedYear] });
      queryClient.invalidateQueries({ queryKey: ["/api/regions"] }); // Refresh regions list if new ones were created
      
      const messages = [`Imported ${result.imported} entries.`];
      if (result.regionsCreated > 0) messages.push(`Created ${result.regionsCreated} new regions.`);
      if (result.errors > 0) messages.push(`${result.errors} errors.`);
      if (skipped > 0) messages.push(`${skipped} skipped (invalid date).`);
      
      toast({ 
        title: "Import Complete", 
        description: messages.join(" ")
      });
      closeImportDialog();
    } catch (error) {
      toast({ title: "Error", description: "Failed to import data.", variant: "destructive" });
    }
    setIsImporting(false);
  };

  const handleTogglePanel = () => {
    setTourPanelOpen(!aiPanelOpen);
  };

  if (entriesLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const draftEntries = scheduleEntries.filter((e: any) => e.isDraft);
  const activeDraftId = activeDraftGroupId ?? (draftEntries.length > 0 ? (draftEntries[0] as any).draftGroupId : undefined);

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 sm:p-4 border-b border-border bg-card">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5" />
          <h2 className="font-semibold">Master Schedule</h2>
          {viewMode === "spreadsheet" && <Badge variant="secondary">{scheduleData.length} days</Badge>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isMobile && (
          <Select value={viewMode} onValueChange={(v) => setViewMode(v as "spreadsheet" | "calendar" | "list" | "tour")}>
            <SelectTrigger className="w-[120px] sm:w-[140px]" data-testid="select-view-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="spreadsheet">Spreadsheet</SelectItem>
              <SelectItem value="calendar">Calendar</SelectItem>
              <SelectItem value="list">List</SelectItem>
              {tourPlannerAllowed && <SelectItem value="tour">Tour</SelectItem>}
            </SelectContent>
          </Select>
          )}
          {/* Mobile: Talent + Year via a single Filters sheet (desktop pickers are spreadsheet-only) */}
          {isMobile && viewMode !== "tour" && (() => {
            const defaultTalentId = talent.find((e) => e.isDefault)?.id ?? talent[0]?.id ?? "";
            return (
              <MobileFilterSheet
                activeCount={
                  (defaultTalentId && selectedTalentId && selectedTalentId !== defaultTalentId ? 1 : 0) +
                  (selectedYear !== 2026 ? 1 : 0)
                }
                chips={[
                  ...(defaultTalentId && selectedTalentId && selectedTalentId !== defaultTalentId
                    ? [{
                        key: "talent",
                        label: talent.find((e) => e.id === selectedTalentId)?.name || "Talent",
                        onRemove: () => setSelectedTalentId(defaultTalentId),
                      }]
                    : []),
                  ...(selectedYear !== 2026
                    ? [{ key: "year", label: String(selectedYear), onRemove: () => setSelectedYear(2026) }]
                    : []),
                ]}
                onClearAll={() => {
                  if (defaultTalentId) setSelectedTalentId(defaultTalentId);
                  setSelectedYear(2026);
                }}
              >
                {talent.length > 0 && (
                  <MobileFilterField label="Talent">
                    <Select value={selectedTalentId} onValueChange={setSelectedTalentId}>
                      <SelectTrigger className="h-11" data-testid="select-mobile-schedule-talent">
                        <SelectValue placeholder="Select talent" />
                      </SelectTrigger>
                      <SelectContent>
                        {talent
                          .filter((e) => e.isActive !== false)
                          .map((e) => (
                            <SelectItem key={e.id} value={e.id} data-testid={`option-mobile-talent-${e.id}`}>
                              {e.name}{e.isDefault ? " (default)" : ""}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </MobileFilterField>
                )}
                <MobileFilterField label="Year">
                  <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger className="h-11" data-testid="select-mobile-schedule-year">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2026">2026</SelectItem>
                      <SelectItem value="2027">2027</SelectItem>
                      <SelectItem value="2028">2028</SelectItem>
                    </SelectContent>
                  </Select>
                </MobileFilterField>
              </MobileFilterSheet>
            );
          })()}
          {viewMode === "spreadsheet" && (
            <>
              {effectiveRole === "admin" && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="button-bulk-actions">
                      Bulk Actions
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleExportSchedule} data-testid="dropdown-export-schedule">
                      <Download className="mr-2 h-4 w-4" />
                      Export CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setIsImportDialogOpen(true)} data-testid="dropdown-import-schedule">
                      <Upload className="mr-2 h-4 w-4" />
                      Import CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setIsImportDialogOpen(true)} data-testid="dropdown-update-schedule">
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Update CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setIsSheetsSyncOpen(true)} data-testid="dropdown-sync-sheets">
                      <FileSpreadsheet className="mr-2 h-4 w-4 text-green-600" />
                      Sync from Google Sheets
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {/* Task #289 — Gear icon: admin-only settings for statuses + columns */}
              {effectiveRole === "admin" && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid="button-schedule-settings">
                      <Settings2 className="w-4 h-4" />
                      <span className="sr-only">Schedule Settings</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setShowStatusAdmin(true)} data-testid="dropdown-manage-statuses">
                      <Settings className="mr-2 h-4 w-4" />
                      Manage Statuses
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowColumnsAdmin(true)} data-testid="dropdown-manage-columns">
                      <Columns className="mr-2 h-4 w-4" />
                      Manage Columns
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {/* Task #289 — Sandbox toggle button (activeDraftId available via closure after loading check) */}
              {viewMode === "spreadsheet" && (
                <Button
                  variant={sandboxMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    if (sandboxMode) {
                      // activeDraftId is the computed value below the entriesLoading check
                      const draftEntries_ = scheduleEntries.filter((e: any) => e.isDraft);
                      const draftId = activeDraftGroupId ?? (draftEntries_.length > 0 ? (draftEntries_[0] as any).draftGroupId : undefined);
                      discardSandboxAndDraft(draftId);
                    } else {
                      openSandbox();
                    }
                  }}
                  className={cn("gap-1.5", sandboxMode && "bg-amber-600 hover:bg-amber-700 border-amber-600")}
                  data-testid="button-sandbox-toggle"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  {sandboxMode ? "Exit Sandbox" : "Sandbox"}
                </Button>
              )}
              {talent.length > 0 && (
                <Select value={selectedTalentId} onValueChange={setSelectedTalentId}>
                  <SelectTrigger className="w-[180px]" data-testid="select-schedule-talent">
                    <SelectValue placeholder="Select talent" />
                  </SelectTrigger>
                  <SelectContent>
                    {talent
                      .filter((e) => e.isActive !== false)
                      .map((e) => (
                        <SelectItem key={e.id} value={e.id} data-testid={`option-talent-${e.id}`}>
                          {e.name}{e.isDefault ? " (default)" : ""}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-[120px]" data-testid="select-schedule-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2026">2026</SelectItem>
                  <SelectItem value="2027">2027</SelectItem>
                  <SelectItem value="2028">2028</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
          {/* AI Tour Planning Panel toggle — Task #289: auto-enters sandbox */}
          {!isMobile && tourPlannerAllowed && (
            <Button
              variant={aiPanelOpen ? "default" : "outline"}
              size="sm"
              onClick={() => {
                if (!sandboxMode) openSandbox();
                handleTogglePanel();
              }}
              className="gap-1.5"
              data-testid="button-toggle-ai-panel"
            >
              <Bot className="w-3.5 h-3.5" />
              Tour AI
            </Button>
          )}
        </div>
      </div>

      {/* Google Sheets Sync Dialog */}
      <SheetsSyncDialog open={isSheetsSyncOpen} onOpenChange={setIsSheetsSyncOpen} />

      {/* Status Admin Dialog — Task #59, Step 9 */}
      <StatusAdminDialog
        open={showStatusAdmin}
        onOpenChange={setShowStatusAdmin}
        statuses={workspaceStatuses}
      />

      {/* Columns Admin Dialog — Task #59, Step 10 */}
      <ColumnsAdminDialog
        open={showColumnsAdmin}
        onOpenChange={setShowColumnsAdmin}
      />

      {/* Region Confirmation Dialog — Task #59, Step 12 */}
      <AlertDialog
        open={pendingRegions !== null && pendingRegions.length > 0}
        onOpenChange={(open) => { if (!open) { setPendingRegions(null); setPendingCommitId(null); } }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>New regions will be added</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRegions && pendingRegions.length === 1
                ? `1 new region will be added to your workspace: "${pendingRegions[0]}". Continue?`
                : `${pendingRegions?.length} new regions will be added to your workspace: ${pendingRegions?.map(r => `"${r}"`).join(", ")}. Continue?`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="button-cancel-region-confirm"
              onClick={() => {
                if (pendingCommitId) {
                  commitDraft.mutate({ draftGroupId: pendingCommitId, createRegions: false });
                }
              }}
            >
              Skip regions
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-region-create"
              onClick={() => {
                if (pendingCommitId) {
                  commitDraft.mutate({ draftGroupId: pendingCommitId, createRegions: true });
                }
              }}
            >
              Add regions &amp; save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Notes Edit Dialog */}
      <Dialog open={notesDialogOpen} onOpenChange={(open) => { setNotesDialogOpen(open); if (!open) { setNotesDialogRow(null); setNotesDialogValue(""); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Notes</DialogTitle>
            {notesDialogRow && (
              <DialogDescription>
                {notesDialogRow.date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="py-2">
            <VoiceToTextTextarea
              value={notesDialogValue}
              onChange={(e) => setNotesDialogValue(e.target.value)}
              placeholder="Enter notes..."
              className="min-h-[120px] resize-y"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleNotesSave();
                }
              }}
              data-testid="input-notes-dialog"
            />
            <p className="text-xs text-muted-foreground mt-1.5">Tip: Press Cmd+Enter (or Ctrl+Enter) to save</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleNotesSave} disabled={updateEntryMutation.isPending || createEntryMutation.isPending} data-testid="button-save-notes">
              {updateEntryMutation.isPending || createEntryMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={(open) => !open && closeImportDialog()}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Import Schedule Data
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file and map columns to fields. Existing entries will be updated, new dates will be created.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>CSV File</Label>
              <div className="flex gap-2">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="flex-1"
                  data-testid="input-import-file"
                />
              </div>
            </div>

            {csvHeaders.length > 0 ? (
              <>
                <div className="text-sm text-muted-foreground">
                  Found {csvDataRows.length} rows to import. Map your CSV columns to schedule fields below.
                </div>

                {!Object.values(fieldMapping).includes("date") && (
                  <div className="p-3 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-400 flex items-center gap-2">
                    <span className="text-sm">You must map a column to "Date" to enable import</span>
                  </div>
                )}

                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">CSV Column</TableHead>
                        <TableHead className="w-[200px]">Sample Value</TableHead>
                        <TableHead className="w-[200px]">Map to Field</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {csvHeaders.map((header, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{header}</TableCell>
                          <TableCell className="text-muted-foreground text-sm truncate max-w-[200px]">
                            {csvDataRows[0]?.[index] || "—"}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={fieldMapping[String(index)] || "__skip__"}
                              onValueChange={(value) => handleMappingChange(String(index), value)}
                            >
                              <SelectTrigger className="w-[180px]" data-testid={`select-mapping-${index}`}>
                                <SelectValue placeholder="Skip column" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__skip__">Skip column</SelectItem>
                                {SCHEDULE_IMPORT_FIELDS.map(field => (
                                  <SelectItem key={field.key} value={field.key}>
                                    {field.label} {field.required ? "*" : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            ) : (
              <div className="bg-muted rounded-lg p-3 text-sm space-y-2">
                <p className="font-medium">Required columns:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li><strong>Date</strong> - Format: MM/DD/YYYY or YYYY-MM-DD</li>
                </ul>
                <p className="font-medium mt-2">Optional columns:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Status, City, State, Option B, Notes</li>
                  <li>Drive Time, Lead Pages, Option C, Option D, Region</li>
                </ul>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={closeImportDialog}
              data-testid="button-cancel-import"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleImport}
              disabled={csvDataRows.length === 0 || !Object.values(fieldMapping).includes("date") || isImporting}
              data-testid="button-confirm-import"
            >
              {isImporting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Import {csvDataRows.length} Records
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task #289 — Sandbox banner (replaces draft banner when sandbox is active) */}
      {viewMode === "spreadsheet" && sandboxMode && (
        <div className="flex items-center justify-between px-4 py-2 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 text-sm shrink-0">
          <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
            <Pencil className="w-4 h-4" />
            <span>
              Sandbox mode — changes are not saved.
              {activeDraftId && <span className="ml-1 opacity-70">({draftEntries.length} staged AI entries)</span>}
              {Object.keys(sandboxEntries).length > 0 && (
                <span className="ml-1 opacity-70">({Object.keys(sandboxEntries).length} manual edits)</span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-amber-400 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900"
              onClick={() => discardSandboxAndDraft(activeDraftId)}
              disabled={isSandboxPublishing || discardDraft.isPending}
              data-testid="button-sandbox-discard"
            >
              {discardDraft.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
              Discard
            </Button>
            <Button
              size="sm"
              className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => publishSandbox(activeDraftId)}
              disabled={isSandboxPublishing}
              data-testid="button-sandbox-publish"
            >
              {isSandboxPublishing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
              Publish to Live
            </Button>
          </div>
        </div>
      )}
      {/* Draft active banner — Task #59 (hidden when sandbox is active, which subsumes it) */}
      {activeDraftId && viewMode === "spreadsheet" && !sandboxMode && (
        <div className="flex items-center justify-between px-4 py-2 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 text-sm shrink-0">
          <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
            <AlertCircle className="w-4 h-4" />
            <span>AI draft in progress — {draftEntries.length} staged entries</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-amber-400 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900"
              onClick={() => discardDraft.mutate(activeDraftId)}
              disabled={discardDraft.isPending}
              data-testid="button-discard-draft"
            >
              {discardDraft.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
              Discard
            </Button>
            <Button
              size="sm"
              className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => previewDraft.mutate(activeDraftId)}
              disabled={previewDraft.isPending || commitDraft.isPending}
              data-testid="button-commit-draft"
            >
              {(previewDraft.isPending || commitDraft.isPending) ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
              Save Draft
            </Button>
          </div>
        </div>
      )}

      {/* Main content area + collapsible AI panel */}
      <div className="flex flex-1 min-h-0 overflow-hidden relative">

      {viewMode === "spreadsheet" && (
        <div className={cn("flex-1 overflow-auto min-w-0", isSelectDragging && "select-none")} ref={tableContainerRef} onScroll={handleTableScroll}>
          <table className="w-full border-collapse text-[13px]">
            <thead className="sticky top-0 bg-muted z-10">
              <tr>
                {mergedColumns.map((col) => (
                  <th 
                    key={col.key}
                    className="border border-border px-2 py-0 text-[11px] text-left font-medium relative group"
                    style={{ width: getColumnWidth(col.key), minWidth: 50 }}
                  >
                    {col.label}
                    <div
                      className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/50 group-hover:bg-border"
                      onMouseDown={(e) => handleResizeStart(e, col.key)}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
            {scheduleData.map((row) => {
              const dateStr = row.date.toISOString();
              const dateKey = row.date.toISOString().slice(0, 10);
              // Task #289 — effective entry merges sandbox overlay over live data
              const eff = getSandboxEntry(row);
              const status = (eff.status as string | undefined) ?? row.entry?.status;
              const rowClass = getRowClassName(status);
              const statusCellClass = getCellClassName(status);
              const isDraftRow = !!(row.entry as any)?.isDraft;
              const runInfo = runsByDate.get(dateKey);
              // Helper: is a given field highlighted in the current selection rectangle?
              const isCellSel = (field: string) => selectedCellKeys.has(`${dateKey}:${field}`);
              // Start/extend selection on mousedown (Shift = extend anchor→end rectangle)
              const onCellMouseDownHandler = (field: string) => (e: React.MouseEvent) => {
                if (e.button !== 0) return;
                if (e.shiftKey && selectedCell) {
                  setSelectionEnd({ dateKey, field });
                  return;
                }
                setSelectedCell({ dateKey, field });
                setSelectionEnd(null);
                setIsSelectDragging(true);
              };
              // Update selectionEnd as the pointer moves across cells during a drag
              const onCellMouseEnterHandler = (field: string) => () => {
                if (isSelectDragging) setSelectionEnd({ dateKey, field });
              };
              const fillHandleMouseDown = (field: string) => (e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                // Custom columns live under customFields, not at the top level of eff
                const val = CORE_ENTRY_FIELDS.has(field)
                  ? String(eff[field] ?? "")
                  : String((eff.customFields as Record<string, any> | undefined)?.[field] ?? "");
                setFillDrag({ startKey: dateKey, field, value: val });
              };

              return (
                <tr
                  key={dateStr}
                  className={cn(
                    "hover:bg-muted/50",
                    rowClass,
                    isDraftRow && "opacity-60 bg-[repeating-linear-gradient(45deg,transparent,transparent_8px,rgba(0,0,0,0.03)_8px,rgba(0,0,0,0.03)_16px)]",
                  )}
                  style={runInfo ? { borderLeft: "2px solid #6366f1" } : undefined}
                  onMouseEnter={() => { if (fillDrag) setFillDragEndKey(dateKey); }}
                >
                  <td className="border border-border px-2 py-0 text-[12px] leading-tight" style={{ width: getColumnWidth("date"), minWidth: 50 }}>
                    <div className="flex items-center gap-1">
                      <span>{formatDate(row.date)}</span>
                      {runInfo && runLabelDateKey.get(runInfo.runId) === dateKey && (
                        <span className="text-[9px] text-indigo-500 dark:text-indigo-400 font-medium whitespace-nowrap">
                          Run·{runInfo.dayCount}d
                        </span>
                      )}
                      {isDraftRow && (
                        <span className="text-[9px] text-amber-500 font-medium">AI</span>
                      )}
                    </div>
                  </td>
                  <td className="border border-border px-2 py-0 text-[12px] leading-tight" style={{ width: getColumnWidth("day"), minWidth: 50 }}>
                    {row.dayOfWeek}
                  </td>
                  <td className={cn("border border-border px-0 py-0", statusCellClass)} style={{ width: getColumnWidth("status"), minWidth: 50 }}>
                    <Select
                      value={status || ""}
                      onValueChange={(v) => handleStatusChange(row, v)}
                    >
                      <SelectTrigger className="h-[18px] text-[11px] border-0 bg-transparent px-1 justify-start" data-testid={`select-status-${dateStr}`}>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <EditableCell
                    value={eff.city as string | undefined}
                    isEditing={editingCell?.date === dateStr && editingCell?.field === "city"}
                    editValue={editValue}
                    onEdit={() => handleCellEdit(dateStr, "city", eff.city as string | undefined)}
                    onChange={setEditValue}
                    onSave={() => handleCellSave(row, "city")}
                    onCancel={() => setEditingCell(null)}
                    onTab={(shiftKey) => handleTabNavigation(dateStr, "city", shiftKey)}
                    testId={`cell-city-${formatDate(row.date).replace(/\//g, "-")}`}
                    width={getColumnWidth("city")}
                    onCopy={() => { navigator.clipboard.writeText(String(eff.city || "")); toast({ title: "Copied" }); }}
                    onPaste={() => handlePaste(row, "city")}
                    isSelected={isCellSel("city")}
                    onCellMouseDown={onCellMouseDownHandler("city")}
                    onCellMouseEnter={onCellMouseEnterHandler("city")}
                    onFillHandleMouseDown={selectedCell?.dateKey === dateKey && selectedCell?.field === "city" ? fillHandleMouseDown("city") : undefined}
                  />
                  <EditableCell
                    value={eff.state as string | undefined}
                    isEditing={editingCell?.date === dateStr && editingCell?.field === "state"}
                    editValue={editValue}
                    onEdit={() => handleCellEdit(dateStr, "state", eff.state as string | undefined)}
                    onChange={setEditValue}
                    onSave={() => handleCellSave(row, "state")}
                    onCancel={() => setEditingCell(null)}
                    onTab={(shiftKey) => handleTabNavigation(dateStr, "state", shiftKey)}
                    testId={`cell-state-${formatDate(row.date).replace(/\//g, "-")}`}
                    width={getColumnWidth("state")}
                    onCopy={() => { navigator.clipboard.writeText(String(eff.state || "")); toast({ title: "Copied" }); }}
                    onPaste={() => handlePaste(row, "state")}
                    isSelected={isCellSel("state")}
                    onCellMouseDown={onCellMouseDownHandler("state")}
                    onCellMouseEnter={onCellMouseEnterHandler("state")}
                    onFillHandleMouseDown={selectedCell?.dateKey === dateKey && selectedCell?.field === "state" ? fillHandleMouseDown("state") : undefined}
                  />
                  {/* Pre-notes custom columns (workspace-configurable via /api/schedule-columns) */}
                  {preNoteCustom.map((col) => {
                    if (col.key === "optionB") return (
                      <EditableCell
                        key="optionB"
                        value={eff.optionB as string | undefined}
                        isEditing={editingCell?.date === dateStr && editingCell?.field === "optionB"}
                        editValue={editValue}
                        onEdit={() => handleCellEdit(dateStr, "optionB", eff.optionB as string | undefined)}
                        onChange={setEditValue}
                        onSave={() => handleCellSave(row, "optionB")}
                        onCancel={() => setEditingCell(null)}
                        onTab={(shiftKey) => handleTabNavigation(dateStr, "optionB", shiftKey)}
                        testId={`cell-optionB-${formatDate(row.date).replace(/\//g, "-")}`}
                        width={getColumnWidth("optionB")}
                        onCopy={() => { navigator.clipboard.writeText(String(eff.optionB || "")); toast({ title: "Copied" }); }}
                        onPaste={() => handlePaste(row, "optionB")}
                        isSelected={isCellSel("optionB")}
                        onCellMouseDown={onCellMouseDownHandler("optionB")}
                        onCellMouseEnter={onCellMouseEnterHandler("optionB")}
                        onFillHandleMouseDown={selectedCell?.dateKey === dateKey && selectedCell?.field === "optionB" ? fillHandleMouseDown("optionB") : undefined}
                      />
                    );
                    const customVal = (eff.customFields as any)?.[col.key] ?? (row.entry as any)?.customFields?.[col.key] ?? "";
                    return (
                      <EditableCell
                        key={col.key}
                        value={customVal}
                        isEditing={editingCell?.date === dateStr && editingCell?.field === col.key}
                        editValue={editValue}
                        onEdit={() => handleCellEdit(dateStr, col.key, customVal)}
                        onChange={setEditValue}
                        onSave={() => handleCellSave(row, col.key)}
                        onCancel={() => setEditingCell(null)}
                        onTab={(shiftKey) => handleTabNavigation(dateStr, col.key, shiftKey)}
                        testId={`cell-${col.key}-${formatDate(row.date).replace(/\//g, "-")}`}
                        width={getColumnWidth(col.key)}
                        onCopy={() => { navigator.clipboard.writeText(customVal ?? ""); toast({ title: "Copied" }); }}
                        onPaste={() => handlePaste(row, col.key)}
                        isSelected={isCellSel(col.key)}
                        onCellMouseDown={onCellMouseDownHandler(col.key)}
                        onCellMouseEnter={onCellMouseEnterHandler(col.key)}
                        onFillHandleMouseDown={selectedCell?.dateKey === dateKey && selectedCell?.field === col.key ? fillHandleMouseDown(col.key) : undefined}
                      />
                    );
                  })}
                  <EditableCell
                    value={eff.notes as string | undefined}
                    isEditing={false}
                    editValue=""
                    onEdit={() => openNotesDialog(row)}
                    onChange={() => {}}
                    onSave={() => {}}
                    onCancel={() => {}}
                    onTab={() => {}}
                    testId={`cell-notes-${formatDate(row.date).replace(/\//g, "-")}`}
                    width={getColumnWidth("notes")}
                    onCopy={() => { navigator.clipboard.writeText(String(eff.notes || "")); toast({ title: "Copied" }); }}
                    onPaste={() => handlePaste(row, "notes")}
                    isSelected={isCellSel("notes")}
                    onCellMouseDown={onCellMouseDownHandler("notes")}
                    onCellMouseEnter={onCellMouseEnterHandler("notes")}
                  />
                  <EditableCell
                    value={eff.driveTime as string | undefined}
                    isEditing={editingCell?.date === dateStr && editingCell?.field === "driveTime"}
                    editValue={editValue}
                    onEdit={() => handleCellEdit(dateStr, "driveTime", eff.driveTime as string | undefined)}
                    onChange={setEditValue}
                    onSave={() => handleCellSave(row, "driveTime")}
                    onCancel={() => setEditingCell(null)}
                    onTab={(shiftKey) => handleTabNavigation(dateStr, "driveTime", shiftKey)}
                    disabled={status !== "Travel Day"}
                    testId={`cell-driveTime-${formatDate(row.date).replace(/\//g, "-")}`}
                    width={getColumnWidth("driveTime")}
                    onCopy={() => { navigator.clipboard.writeText(String(eff.driveTime || "")); toast({ title: "Copied" }); }}
                    onPaste={() => handlePaste(row, "driveTime")}
                    isSelected={isCellSel("driveTime")}
                    onCellMouseDown={onCellMouseDownHandler("driveTime")}
                    onCellMouseEnter={onCellMouseEnterHandler("driveTime")}
                    onFillHandleMouseDown={selectedCell?.dateKey === dateKey && selectedCell?.field === "driveTime" ? fillHandleMouseDown("driveTime") : undefined}
                  />
                  {/* Post-drive custom columns (workspace-configurable via /api/schedule-columns) */}
                  {postDriveCustom.map((col) => {
                    if (col.key === "leadPages") return (
                      <EditableCell
                        key="leadPages"
                        value={(eff.leadPages as number | undefined)?.toString()}
                        isEditing={editingCell?.date === dateStr && editingCell?.field === "leadPages"}
                        editValue={editValue}
                        onEdit={() => handleCellEdit(dateStr, "leadPages", (eff.leadPages as number | undefined)?.toString())}
                        onChange={setEditValue}
                        onSave={() => handleCellSave(row, "leadPages")}
                        onCancel={() => setEditingCell(null)}
                        onTab={(shiftKey) => handleTabNavigation(dateStr, "leadPages", shiftKey)}
                        type="number"
                        testId={`cell-leadPages-${formatDate(row.date).replace(/\//g, "-")}`}
                        width={getColumnWidth("leadPages")}
                        onCopy={() => { navigator.clipboard.writeText(String(eff.leadPages || "")); toast({ title: "Copied" }); }}
                        onPaste={() => handlePaste(row, "leadPages")}
                        isSelected={isCellSel("leadPages")}
                        onCellMouseDown={onCellMouseDownHandler("leadPages")}
                        onCellMouseEnter={onCellMouseEnterHandler("leadPages")}
                        onFillHandleMouseDown={selectedCell?.dateKey === dateKey && selectedCell?.field === "leadPages" ? fillHandleMouseDown("leadPages") : undefined}
                      />
                    );
                    if (col.key === "estDays") return (
                      <td key="estDays" className="border border-border px-2 py-0 text-[12px] leading-tight bg-muted/30" style={{ width: getColumnWidth("estDays"), minWidth: 50 }} data-testid={`cell-estDays-${formatDate(row.date).replace(/\//g, "-")}`}>
                        {calculateEstDays(eff.leadPages as number | undefined).toFixed(2)}
                      </td>
                    );
                    const postCustomVal = (eff.customFields as any)?.[col.key] ?? (row.entry as any)?.customFields?.[col.key] ?? "";
                    return (
                      <EditableCell
                        key={col.key}
                        value={postCustomVal}
                        isEditing={editingCell?.date === dateStr && editingCell?.field === col.key}
                        editValue={editValue}
                        onEdit={() => handleCellEdit(dateStr, col.key, postCustomVal)}
                        onChange={setEditValue}
                        onSave={() => handleCellSave(row, col.key)}
                        onCancel={() => setEditingCell(null)}
                        onTab={(shiftKey) => handleTabNavigation(dateStr, col.key, shiftKey)}
                        testId={`cell-${col.key}-${formatDate(row.date).replace(/\//g, "-")}`}
                        width={getColumnWidth(col.key)}
                        onCopy={() => { navigator.clipboard.writeText(postCustomVal ?? ""); toast({ title: "Copied" }); }}
                        onPaste={() => handlePaste(row, col.key)}
                        isSelected={isCellSel(col.key)}
                        onCellMouseDown={onCellMouseDownHandler(col.key)}
                        onCellMouseEnter={onCellMouseEnterHandler(col.key)}
                        onFillHandleMouseDown={selectedCell?.dateKey === dateKey && selectedCell?.field === col.key ? fillHandleMouseDown(col.key) : undefined}
                      />
                    );
                  })}
                  <td 
                    className={cn(
                      "border border-border px-2 py-0 text-[12px] leading-tight",
                      row.invoiceTotal > 0 && row.invoiceTotal < 500 && "bg-red-200 dark:bg-red-900/50",
                      row.invoiceTotal >= 500 && row.invoiceTotal < 700 && "bg-yellow-200 dark:bg-yellow-700/50",
                      row.invoiceTotal >= 700 && "bg-green-200 dark:bg-green-900/50",
                      row.invoiceTotal === 0 && "bg-muted/30"
                    )}
                    style={{ width: getColumnWidth("invoiceTotal"), minWidth: 50 }} 
                    data-testid={`cell-invoiceTotal-${formatDate(row.date).replace(/\//g, "-")}`}
                  >
                    {row.invoiceTotal > 0 ? `$${row.invoiceTotal.toFixed(2)}` : ""}
                  </td>
                  <td
                    className={cn(
                      "border border-border px-0 py-0",
                      isCellSel("region") && "ring-2 ring-inset ring-primary/70 bg-primary/5"
                    )}
                    style={{ width: getColumnWidth("region"), minWidth: 50 }}
                    onMouseDown={(e) => onCellMouseDownHandler("region")(e)}
                    onMouseEnter={onCellMouseEnterHandler("region")}
                  >
                    <Popover 
                      open={openRegionPopover === dateStr} 
                      onOpenChange={(open) => setOpenRegionPopover(open ? dateStr : null)}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          role="combobox"
                          className="h-[18px] w-full justify-between px-2 text-[11px] font-normal rounded-none border-0"
                          data-testid={`select-region-${formatDate(row.date).replace(/\//g, "-")}`}
                        >
                          <span className="flex items-center gap-1 truncate min-w-0">
                            {/* regionHint = AI flagged this row as needing a new region */}
                            {(row.entry as any)?.regionHint && !eff.regionId && (
                              <AlertCircle className="h-2.5 w-2.5 shrink-0 text-amber-500" title={`Pending region: ${(row.entry as any).regionHint}`} />
                            )}
                            <span className="truncate">
                              {(eff.regionId as string | undefined)
                                ? regions.find((r) => r.id === (eff.regionId as string))?.name || "Select..."
                                : "Select..."}
                            </span>
                          </span>
                          <ChevronsUpDown className="ml-1 h-2.5 w-2.5 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-48 p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search regions..." className="h-8 text-xs" />
                          <CommandList>
                            <CommandEmpty>No region found.</CommandEmpty>
                            <CommandGroup>
                              <CommandItem
                                value="none"
                                onSelect={() => {
                                  handleRegionChange(row, null);
                                  setOpenRegionPopover(null);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-3 w-3",
                                    !row.entry?.regionId ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                None
                              </CommandItem>
                              {regions.map((region) => (
                                <CommandItem
                                  key={region.id}
                                  value={region.name}
                                  onSelect={() => {
                                    handleRegionChange(row, region.id);
                                    setOpenRegionPopover(null);
                                    setAddingRegionForRow(null);
                                    setNewRegionName("");
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-3 w-3",
                                      row.entry?.regionId === region.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {region.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                            <div className="border-t border-border p-1">
                              {addingRegionForRow === formatDate(row.date) ? (
                                <div className="flex items-center gap-1 px-1 py-0.5">
                                  <Input
                                    autoFocus
                                    value={newRegionName}
                                    onChange={(e) => setNewRegionName(e.target.value)}
                                    placeholder="City, ST"
                                    className="h-6 text-xs flex-1 px-1.5"
                                    onKeyDown={async (e) => {
                                      if (e.key === "Enter" && newRegionName.trim()) {
                                        const created = await createRegionMutation.mutateAsync(newRegionName.trim()) as any;
                                        handleRegionChange(row, created.id);
                                        setOpenRegionPopover(null);
                                        setAddingRegionForRow(null);
                                        setNewRegionName("");
                                      } else if (e.key === "Escape") {
                                        setAddingRegionForRow(null);
                                        setNewRegionName("");
                                      }
                                    }}
                                  />
                                  <Button
                                    size="sm"
                                    className="h-6 px-1.5 text-xs"
                                    disabled={!newRegionName.trim() || createRegionMutation.isPending}
                                    onClick={async () => {
                                      if (!newRegionName.trim()) return;
                                      const created = await createRegionMutation.mutateAsync(newRegionName.trim()) as any;
                                      handleRegionChange(row, created.id);
                                      setOpenRegionPopover(null);
                                      setAddingRegionForRow(null);
                                      setNewRegionName("");
                                    }}
                                  >
                                    {createRegionMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 px-1.5 text-xs"
                                    onClick={() => { setAddingRegionForRow(null); setNewRegionName(""); }}
                                  >✕</Button>
                                </div>
                              ) : (
                                <button
                                  className="flex items-center gap-1.5 w-full px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-accent rounded-sm transition-colors"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setAddingRegionForRow(formatDate(row.date));
                                    setNewRegionName("");
                                  }}
                                >
                                  <Plus className="h-3 w-3" />
                                  Add region
                                </button>
                              )}
                            </div>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </td>
                </tr>
              );
            })}
            </tbody>
          </table>
        </div>
      )}

      {/* Tour View — Task #611 */}
      {viewMode === "tour" && tourPlannerAllowed && (
        <div className="flex-1 min-h-0 overflow-hidden">
          <Suspense fallback={<div className="flex items-center justify-center h-full"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
            <TourPlanner selectedTalentId={selectedTalentId} selectedYear={selectedYear} effectiveRole={effectiveRole} />
          </Suspense>
        </div>
      )}


      {/* Calendar View */}
      {viewMode === "calendar" && (
        <div className="flex-1 overflow-auto p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={goToPrevMonth} data-testid="button-prev-month">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={goToNextMonth} data-testid="button-next-month">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <h3 className="font-semibold ml-2">{currentMonthLabel}</h3>
            </div>
            <Button variant="outline" size="sm" onClick={goToToday} data-testid="button-go-to-today">
              Today
            </Button>
          </div>
          
          <div className="grid grid-cols-7 gap-0 border border-border rounded-lg overflow-hidden">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium bg-muted border-b border-border">
                {day}
              </div>
            ))}
            {calendarDays.map((dayData, idx) => {
              const isCurrentMonth = dayData.isCurrentMonth;
              const isToday = dayData.isToday;
              const entry = dayData.entry;
              const status = entry?.status;
              
              return (
                <div 
                  key={idx}
                  className={cn(
                    "min-h-[100px] p-2 border-b border-r border-border",
                    !isCurrentMonth && "bg-muted/50 text-muted-foreground",
                    isToday && "ring-2 ring-primary ring-inset"
                  )}
                  data-testid={`calendar-day-${dayData.date.toISOString().split('T')[0]}`}
                >
                  <div className="font-medium text-sm mb-1">{dayData.date.getDate()}</div>
                  {entry && (
                    <div className="space-y-1">
                      {status && (
                        <Badge 
                          variant="secondary" 
                          className={cn(
                            "text-xs w-full justify-center",
                            status === "Public Show" && "bg-green-500/20 text-green-700 dark:text-green-400",
                            status === "SL Show" && "bg-green-500/20 text-green-700 dark:text-green-400",
                            status === "Day Off" && "bg-orange-500/20 text-orange-700 dark:text-orange-400",
                            status === "Travel Day" && "bg-yellow-500/20 text-yellow-700 dark:text-yellow-400",
                            status === "Outside Show" && "bg-blue-500/20 text-blue-700 dark:text-blue-400"
                          )}
                        >
                          {status}
                        </Badge>
                      )}
                      {entry.city && (
                        <div className="text-xs text-muted-foreground truncate">
                          {entry.city}{entry.state ? `, ${entry.state}` : ""}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Month Statistics */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(currentMonthStats).map(([status, count]) => (
              <Card key={status}>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">{count}</div>
                  <div className="text-sm text-muted-foreground">{status}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === "list" && (
        <div className="flex-1 overflow-auto p-3 sm:p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" className="h-11 w-11" onClick={goToPrevMonth} data-testid="button-prev-month-list">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-11 w-11" onClick={goToNextMonth} data-testid="button-next-month-list">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <h3 className="font-semibold ml-1">{currentMonthLabel}</h3>
            </div>
            <Button variant="outline" onClick={goToToday} data-testid="button-go-to-today-list">
              Today
            </Button>
          </div>

          {/* Compact week strip — mobile only, lets users jump between weeks */}
          {isMobile && (() => {
            const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
            const weeks: number[][] = [];
            let week: number[] = [];
            for (let d = 1; d <= daysInMonth; d++) {
              week.push(d);
              if (week.length === 7 || d === daysInMonth) { weeks.push(week); week = []; }
            }
            return (
              <div className="flex gap-1 overflow-x-auto pb-2 mb-3 -mx-3 px-3 scrollbar-hide" data-testid="week-strip">
                {weeks.map((wk, wi) => {
                  const firstDay = wk[0];
                  const dateObj = new Date(selectedYear, selectedMonth, firstDay);
                  const isCurrentWeek = wk.some(d => {
                    const dd = new Date(selectedYear, selectedMonth, d);
                    return dd.toDateString() === today.toDateString();
                  });
                  return (
                    <button
                      key={wi}
                      className={cn(
                        "flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium min-w-[56px] text-center border transition-colors",
                        isCurrentWeek
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-card border-border text-foreground hover:bg-muted"
                      )}
                      onClick={() => {
                        const el = document.querySelector(`[data-date-day="${firstDay}"]`);
                        el?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                      data-testid={`week-strip-week-${wi}`}
                    >
                      {dateObj.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}
                      <div className="text-[10px] opacity-70">W{wi + 1}</div>
                    </button>
                  );
                })}
              </div>
            );
          })()}
          
          <div className="space-y-2">
            {(() => {
              // Use scheduleData (local-time Dates) to avoid UTC-to-local timezone shift
              const mobileRows = scheduleData.filter(row =>
                row.date.getFullYear() === selectedYear &&
                row.date.getMonth() === selectedMonth &&
                row.entry
              );
              if (mobileRows.length === 0) {
                return (
                  <div className="text-center py-8 text-muted-foreground">
                    No scheduled events for this month
                  </div>
                );
              }
              return mobileRows.map((row, idx) => {
                const itemDate = row.date;
                const item = row.entry!;
                const invoiceTotal = row.invoiceTotal;
                const isToday = itemDate.toDateString() === today.toDateString();

                return (
                  <Card key={idx} className={cn(isToday && "border-primary")} data-testid={`list-item-${idx}`} data-date-day={itemDate.getDate()}>
                    <CardContent className="py-3 px-3 sm:px-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <div className={cn("text-center min-w-[44px] h-[44px] flex flex-col items-center justify-center rounded-lg", isToday ? "bg-primary text-primary-foreground" : "bg-muted")}>
                            <div className="font-bold text-base leading-none">{itemDate.getDate()}</div>
                            <div className="text-[10px] uppercase mt-0.5">
                              {itemDate.toLocaleDateString('en-US', { weekday: 'short' })}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">
                              {item.city || "No location"}{item.state ? `, ${item.state}` : ""}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {itemDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          {item.status && (
                            <Badge
                              variant="secondary"
                              className={cn(
                                "text-[10px] sm:text-xs",
                                item.status === "Public Show" && "bg-green-500/20 text-green-700 dark:text-green-400",
                                item.status === "SL Shows" && "bg-green-500/20 text-green-700 dark:text-green-400",
                                item.status === "Day Off" && "bg-orange-500/20 text-orange-700 dark:text-orange-400",
                                item.status === "Travel Day" && "bg-yellow-500/20 text-yellow-700 dark:text-yellow-400",
                                item.status === "Outside Show" && "bg-blue-500/20 text-blue-700 dark:text-blue-400"
                              )}
                            >
                              {item.status}
                            </Badge>
                          )}
                          {invoiceTotal > 0 && (
                            <span className="text-[10px] sm:text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                              ${invoiceTotal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </span>
                          )}
                        </div>
                      </div>
                      {item.notes && (
                        <div className="mt-2 text-xs text-muted-foreground border-t border-border pt-2">
                          {item.notes}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Tour Planning AI Panel — Task #59 */}
      {!isMobile && (
        <Suspense fallback={null}>
          <TourPlanningPanel
            open={aiPanelOpen}
            onToggle={handleTogglePanel}
            year={selectedYear}
            statusOptions={statusOptions}
            runSummary={runSummary}
            activeDraftGroupId={activeDraftId}
            onDraftGroupIdChange={setActiveDraftGroupId}
            talentId={selectedTalentId || null}
          />
        </Suspense>
      )}
    </div>
  </div>
  );
}

interface EditableCellProps {
  value: string | null | undefined;
  isEditing: boolean;
  editValue: string;
  onEdit: () => void;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onTab?: (shiftKey: boolean) => void;
  disabled?: boolean;
  type?: "text" | "number";
  width?: number;
  onCopy?: () => void;
  onPaste?: () => void;
  // Task #289 — Cell selection
  isSelected?: boolean;
  onSelect?: () => void;
  onCellMouseDown?: (e: React.MouseEvent) => void;
  onCellMouseEnter?: () => void;
  onFillHandleMouseDown?: (e: React.MouseEvent) => void;
}

function EditableCell({
  value,
  isEditing,
  editValue,
  onEdit,
  onChange,
  onSave,
  onCancel,
  onTab,
  disabled = false,
  type = "text",
  testId,
  width,
  onCopy,
  onPaste,
  isSelected = false,
  onSelect,
  onCellMouseDown,
  onCellMouseEnter,
  onFillHandleMouseDown,
}: EditableCellProps & { testId?: string }) {
  const cellStyle = width ? { width, minWidth: 50 } : undefined;
  
  if (disabled) {
    return (
      <td className="border border-border px-2 py-0 text-[12px] leading-tight bg-muted/20 cursor-not-allowed" style={cellStyle} data-testid={testId}>
        {value || ""}
      </td>
    );
  }

  if (isEditing) {
    return (
      <td className="border border-border px-1 py-0" style={cellStyle}>
        <Input
          value={editValue}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => {
            if (!e.relatedTarget) {
              onSave();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSave();
            if (e.key === "Escape") onCancel();
            if (e.key === "Tab") {
              e.preventDefault();
              onSave();
              if (onTab) {
                onTab(e.shiftKey);
              }
            }
          }}
          type={type}
          className="text-[11px] h-[18px] py-0"
          autoFocus
          data-testid={testId ? `${testId}-input` : undefined}
        />
      </td>
    );
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <td
          className={cn(
            "border border-border px-2 py-0 text-[12px] leading-tight cursor-pointer hover:bg-muted/50",
            isSelected && "ring-2 ring-inset ring-primary/70 bg-primary/5"
          )}
          style={{ ...cellStyle, position: isSelected ? "relative" : undefined }}
          onMouseDown={(e) => { if (e.button === 0) onCellMouseDown?.(e); }}
          onMouseEnter={onCellMouseEnter}
          onDoubleClick={onEdit}
          data-testid={testId}
        >
          {value || ""}
          {/* Task #289 — Fill handle */}
          {isSelected && onFillHandleMouseDown && (
            <div
              className="absolute bottom-0 right-0 w-2 h-2 bg-primary cursor-crosshair z-10"
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onFillHandleMouseDown(e); }}
              data-testid="fill-handle"
            />
          )}
        </td>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onClick={onCopy} data-testid="context-menu-copy">
          <Copy className="w-4 h-4 mr-2" />
          Copy
        </ContextMenuItem>
        <ContextMenuItem onClick={onPaste} data-testid="context-menu-paste">
          <ClipboardPaste className="w-4 h-4 mr-2" />
          Paste
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
